/* WL — Wall Murals page module (v5 "real rooms", 2026-07-17 — Summer's round).
   The demo is REAL ROOM PHOTOGRAPHY (fal-generated empty rooms, wall planes calibrated
   by hand) — the mural is warped onto the actual walls, never stretched: each photo wall
   carries a nominal real size and shows the first metres of your wall at true proportion.
   ONE view (Room) + My room + AI photo. Compact: no page title, themes are a dropdown
   (curated + your own saved themes), search/grid on the LEFT; wall sizes, millimetre cut
   RULERS, colour & mood, material, price on the RIGHT. Drag the strip under the photo to
   reorder artworks. Blend maths = mural_merge.render_recipe_v2/3 (change both together).
   Deploy: python3 wl_deploy.py mural */
(() => {
  const root = document.getElementById("wl-mural-root");
  if (!root || root.dataset.wlm) return;
  root.dataset.wlm = "1";

  const API = "https://wl-studio.wallliberation.workers.dev";
  // P0 field diagnostics (2026-07-20, phone display failure): any crash on a customer
  // device paints a visible message AND beacons the exact error home — a broken page
  // must never be silent again. Registered FIRST so even boot crashes are caught.
  const DIAG = m => { try {
    fetch(API + "/api/diag", { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ m: String(m).slice(0, 500), ua: navigator.userAgent.slice(0, 140),
        w: innerWidth, dpr: devicePixelRatio || 1 }) }); } catch (e) {} };
  // The !root.childElementCount guard means this message only appears when the
  // designer never drew anything. An audit on 2026-08-24 called it dead code,
  // because the shell is written into root synchronously further down, so the
  // guard is false for every error after that point. Deliberately LEFT AS IS:
  // making it fire later would replace a working designer with an error message
  // on any stray late error, which is far worse than staying quiet. The case
  // that genuinely needed covering — the script never arriving at all, where
  // this listener cannot run because it is inside the file that failed — is
  // handled outside the file, in sections/wl-mural-page.liquid.
  window.addEventListener("error", e => { DIAG((e.message || e.type) + " @" +
      String(e.filename || "").split("/").pop() + ":" + e.lineno);
    if (!root.childElementCount) root.innerHTML =
      '<p style="padding:24px 16px;font-size:14px">The mural designer hit a problem on this device — ' +
      'we have been notified automatically. Please refresh, or email us and we will design it with you.</p>'; });
  window.addEventListener("unhandledrejection", e =>
    DIAG("promise: " + ((e.reason && (e.reason.message || e.reason.stack)) || e.reason)));
  const GREEN = "#4a5230";
  const TSK = "0x4AAAAAAD0-wshBVMBagcB3";
  const PAGE_SIZE = 24, MAX_WALL = 1000, MAX_H = 600;

  // Real room templates: photo + wall planes (quads as image fractions, TL,TR,BR,BL)
  // + each photo wall's nominal real width in cm (prevents any stretching of the art).
  // The demo room is PARAMETRIC: geometry built from the customer's real wall sizes
  // (the wall extends — the art is never squeezed), dressed with photographic textures.
  const FLOOR_TX = "/rooms3d/tx_floor.jpg";   // real oak floor sampled from our room photography

  // ---------- state ----------
  const MC = { members: [], mood: "natural", tone: { b: 1, c: 1, s: 1 }, h: 270,
               walls: [{ w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }],
               material: "matte_canvas", panel: 100, grid: true, offset: 0.5,
               tab: "tpl", sel: 0,
               ownSrc: null, ownQuad: null, ownH: 240, ownOff: 0, ownEdit: false, aiUrl: null };
  // Pre-designed PDPs keep their OWN state pocket (2026-07-30): a curated product must
  // open PRISTINE — the visitor's main-designer session (mood, walls, openings, panels)
  // must never leak into it, and edits on one product must never touch another.
  const SEED0 = (root.dataset.seed || new URLSearchParams(location.search).get("seed") || "").trim();
  const SKEY = SEED0 ? "wl_mural_p_" + SEED0.split(/[\s,]+/).join("-").slice(0, 90) : "wl_mural_state4";
  const HERO = new URLSearchParams(location.search).has("wlmhero");   // hero-capture mode: clean frame, no persist
  // Mural Gallery 2026-08-20: single-artwork PDPs open a pared-back guided flow —
  // wall size first, art fixed; expert mode ("Bespoke Studio") is the full workshop.
  const SIMPLE = root.dataset.mode === "simple" && !HERO;
  const MAXW = Math.max(0, +(root.dataset.maxw || 0));   // widest this artwork prints ALONE (cm), advisory
  let PDP_FRESH = !!SEED0;   // stays true when there is no usable saved pocket → boot loads the curated design
  try { const s = JSON.parse(localStorage[SKEY] || "{}");
    if (SEED0 && (!(s.members || []).length || Date.now() - (+s._ts || 0) > 12 * 3600e3)) {
      /* first visit or stale pocket — keep MC at curated defaults */
    } else {
      if (SEED0) PDP_FRESH = false;
      delete s._ts; delete s.tab; delete s.aiUrl; delete s.ownSrc; delete s.ownQuad; delete s.ownOff; Object.assign(MC, s); } } catch (e) {}
  if (!Array.isArray(MC.walls) || !MC.walls.length) MC.walls = [{ w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }];
  if (MC.panel === "one") MC.panel = 100;   // one-seamless option retired 2026-07-20
  // Freshness rule (Summer 2026-07-20: auto-restoring days-old edits is
  // counterintuitive): a session left mid-task resumes SILENTLY for 12 h; anything
  // older opens FRESH (curated example) and the old design becomes a one-tap
  // "Resume" offer — good first impression, nothing ever lost. Saved themes (max 3)
  // remain the customer's explicit long-term saves.
  try {
    const age = Date.now() - (+localStorage.wl_mural_ts || 0);
    if (!SEED0 && (MC.members || []).length && age > 12 * 3600e3) {
      localStorage.wl_mural_resume = localStorage.wl_mural_state4;
      MC.members = []; MC.mood = "natural"; MC.tone = { b: 1, c: 1, s: 1 };
      MC.h = 270; MC.panel = 100; MC.offset = .5; MC.material = "matte_canvas";
      MC.walls = [{ w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }];
    }
  } catch (e) {}
  if (!MC.tone) MC.tone = { b: 1, c: 1, s: 1 };
  MC.walls = MC.walls.slice(0, 4); MC.sel = Math.min(MC.sel || 0, MC.walls.length - 1);
  MC.yaw = 0;   // look-around angle (session only)
  MC.zoom = 1;  // framed-view dolly factor, wheel/pinch/buttons (session only)
  const persist = () => { if (HERO) return; try { localStorage.wl_mural_ts = String(Date.now());
    localStorage[SKEY] = JSON.stringify({ _ts: Date.now(),
    members: MC.members, mood: MC.mood, tone: MC.tone, h: MC.h, walls: MC.walls,
    material: MC.material, panel: MC.panel, grid: MC.grid, offset: MC.offset }); } catch (e) {} };
  const totalW = () => MC.walls.reduce((a, w) => a + (+w.w || 0), 0);

  let DESIGNS = [], STATS = {}, THEMES = [], SR = null, QUOTE = null, EMAIL = localStorage.wl_email || "", PAGE = 1, THEME = "";
  const F = { artist: "", style: "", colours: [] };
  const byI = i => DESIGNS.find(d => d.i === i);
  const myThemes = () => { try { return JSON.parse(localStorage.wl_my_themes || "[]"); } catch (e) { return []; } };

  const MMOODS = { natural: [0, 0, 0], rosegold: [22, 4, -13], jade: [-15, 6, 9], sepia: [18, 2, -20], indigo: [-18, -4, 16] };
  const MOOD_TINT = { natural: "#b9b4ae", rosegold: "#c9a08e", jade: "#93b3a2", sepia: "#b8a88a", indigo: "#8a92b8" };
  const MOOD_NAME = { natural: "Natural", rosegold: "Rose Gold", jade: "Jade", sepia: "Sepia", indigo: "Indigo" };
  const MATS = {   // REAL supplier data (2026-07-15 sheet): gsm + roll widths are facts
    matte_canvas: ["Matte Canvas", "280 gsm synthetic-fibre canvas, soft matte face — tough, slightly elastic, suits every image. Seamless in one piece up to 320 cm height.", 320],
    velvet_fabric: ["Velvet-Back Mural Fabric", "380 gsm woven texture with a velvet back that grips the wall — made for murals, easy to hang, holds firm. Seamless up to 320 cm height.", 320],
    silk_fabric: ["Silk-Sheen Mural Fabric", "280 gsm with a fine silk-like lustre — the smoothest face, for delicate detail. Velvet back for easy hanging. Seamless up to 320 cm height.", 320],
    artist_linen: ["Artist Linen Canvas", "Real 500 gsm artist linen — your mural printed on raw professional canvas with the true linen weave, and you can paint over it like any artist canvas. UV-cured inks. Seamless up to 315 cm height. Priced at twice the standard rate.", 315] };
  const DOTS = { Black: "#1b1b1b", White: "#f4f1ec", Grey: "#9a9a9a", Brown: "#7a5236", Gold: "#c8a24a", Yellow: "#e8c33c",
    Orange: "#d97b2e", Red: "#b23230", Pink: "#d98ba0", Purple: "#7d5a9e", Blue: "#3b5f8f", Aqua: "#5fa8a0", Green: "#4b7a4f", Olive: "#7c7a3f" };
  const STR = 0.55, OVF = 0.22, COMP_H = 800;   // blend constants — mirrored in mural_merge.py

  // ---------- utils ----------
  const $ = (q, el) => (el || root).querySelector(q);
  const $$ = (q, el) => [...(el || root).querySelectorAll(q)];
  const disp = (q, v) => { const e = $(q); if (e) e.style.display = v; }; // null-safe show/hide — ids can retire (the #wlmSel lesson)
  // r3 (Summer 2026-08-01): never show A$ to intl — engine quotes are AUD,
  // convert at the presented rate (checkout rounds up to the dollar, ceil mirrors it)
  const WLM_CUR = (() => { try { const c = window.Shopify && Shopify.currency;
    return c && c.active && c.active !== "AUD" ? c.active : null; } catch (e) { return null; } })();
  const WLM_RATE = (() => { try { const c = window.Shopify && Shopify.currency;
    return c && c.rate ? +c.rate : 1; } catch (e) { return 1; } })();
  // the visitor's own symbol, same formatter the artwork pages use (wlFmt).
  // Hardcoding "$" printed "$3,044 EUR" and "$2,605 GBP" on the mural pages —
  // right number, wrong symbol, and it matched nothing else on the page
  // (Summer 2026-08-23).
  const wlmFmt = v => { try {
      return new Intl.NumberFormat("en", { style: "currency", currency: WLM_CUR,
        minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(v)); }
    catch (e) { return "$" + Number(v).toLocaleString("en-US"); } };
  const money = v => WLM_CUR
    ? wlmFmt(Math.ceil(Number(v) * WLM_RATE)) + " " + WLM_CUR
    : "A$" + Number(v).toLocaleString("en-AU");
  const esc = s => String(s || "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  let toastEl = null;
  function toast(m, ms) { if (!toastEl) { toastEl = document.createElement("div");
      toastEl.style.cssText = "position:fixed;left:50%;bottom:26px;transform:translateX(-50%);background:#1b1b1b;color:#fff;padding:10px 18px;font-size:13px;z-index:9000;display:none;max-width:86vw;text-align:center";
      document.body.appendChild(toastEl); }
    toastEl.textContent = m; toastEl.style.display = "block";
    clearTimeout(toastEl._h); toastEl._h = setTimeout(() => toastEl.style.display = "none", ms || 3400); }
  const IMGCACHE = {};
  const loadImg = src => IMGCACHE[src] || (IMGCACHE[src] = new Promise(res => { const im = new Image();
    if (!String(src).startsWith("data:")) im.crossOrigin = "anonymous";
    im.onload = () => res(im); im.onerror = () => { delete IMGCACHE[src]; res(null); }; im.src = src; }));
  const tsGet = () => new Promise(res => { try {
    const run = () => { const host = document.createElement("div");
      host.style.cssText = "position:fixed;bottom:14px;right:14px;z-index:2147483000"; document.body.appendChild(host);
      let done = false; const fin = t => { if (done) return; done = true; setTimeout(() => { try { host.remove(); } catch (e) {} }, 600); res(t || null); };
      try { window.turnstile.render(host, { sitekey: TSK, appearance: "interaction-only", callback: fin, "error-callback": () => fin(null), "expired-callback": () => fin(null) }); } catch (e) { fin(null); }
      setTimeout(() => fin(null), 30000); };
    if (window.turnstile) run();
    else { const s = document.createElement("script"); s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.onload = run; s.onerror = () => res(null); document.head.appendChild(s); } } catch (e) { res(null); } });
  async function api(p, body) {
    if (body && (p === "ai-mockup" || p === "enquiry")) body.ts = await tsGet();
    const r = await fetch(API + "/api/" + p, body ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {});
    return r.json(); }
  let _pf = null;
  function pickFile(cb) {
    if (!_pf) { _pf = document.createElement("input"); _pf.type = "file";
      _pf.style.cssText = "position:absolute;width:1px;height:1px;opacity:0;pointer-events:none"; document.body.appendChild(_pf); }
    _pf.accept = "image/*,.heic,.heif"; _pf.value = "";
    _pf.onchange = () => { const f = _pf.files[0]; if (!f) return;
      if (f.size > 20 * 1024 * 1024) { toast("Photographs up to 20 MB, please — larger files by email."); return; }
      const isHeic = /heic|heif/i.test((f.type || "") + " " + (f.name || ""));
      toast("Loading your photo…");
      const fin = im => { const s = Math.min(1, 1600 / Math.max(im.width, im.height));
        const cv = document.createElement("canvas"); cv.width = Math.round(im.width * s); cv.height = Math.round(im.height * s);
        cv.getContext("2d").drawImage(im, 0, 0, cv.width, cv.height); cb(cv.toDataURL("image/jpeg", .88)); };
      const fb = () => { const r = new FileReader();
        r.onload = () => { const im = new Image(); im.onload = () => fin(im);
          im.onerror = () => toast(isHeic ? "That is an iPhone HEIC photo, which this browser cannot open — export it as JPEG (or screenshot it) and upload that." : "Could not open that picture — a JPEG or PNG works best.", 6000);
          im.src = r.result; };
        r.onerror = () => toast("Could not read that file — please try again."); r.readAsDataURL(f); };
      if (window.createImageBitmap) createImageBitmap(f, { imageOrientation: "from-image" }).catch(() => createImageBitmap(f)).then(fin).catch(fb); else fb(); };
    _pf.click(); }

  // ---------- geometry ----------
  function solveH(src, dst) { const A = [], b = [];
    for (let i = 0; i < 4; i++) { const [x, y] = src[i], [u, v] = dst[i];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u); A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v); }
    for (let c = 0; c < 8; c++) { let p = c; for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
      [A[c], A[p]] = [A[p], A[c]]; [b[c], b[p]] = [b[p], b[c]];
      for (let r = 0; r < 8; r++) { if (r === c || !A[c][c]) continue; const f = A[r][c] / A[c][c];
        for (let k = c; k < 8; k++) A[r][k] -= f * A[c][k]; b[r] -= f * b[c]; } }
    const h = b.map((v, i) => v / A[i][i]); return [...h, 1]; }
  const applyH = (h, x, y) => { const w = h[6] * x + h[7] * y + h[8]; return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w]; };
  function drawWarped(ctx, img, quad, grid = 12) {
    const H = solveH([[0, 0], [img.width, 0], [img.width, img.height], [0, img.height]], quad);
    const sx = img.width / grid, sy = img.height / grid;
    for (let i = 0; i < grid; i++) for (let j = 0; j < grid; j++) {
      const s = [[i * sx, j * sy], [(i + 1) * sx, j * sy], [(i + 1) * sx, (j + 1) * sy], [i * sx, (j + 1) * sy]];
      const d = s.map(p => applyH(H, p[0], p[1]));
      for (const tri of [[0, 1, 2], [0, 2, 3]]) { const st = tri.map(k => s[k]), dt = tri.map(k => d[k]);
        ctx.save(); ctx.beginPath(); ctx.moveTo(dt[0][0], dt[0][1]); ctx.lineTo(dt[1][0], dt[1][1]); ctx.lineTo(dt[2][0], dt[2][1]); ctx.closePath(); ctx.clip();
        const [[x0, y0], [x1, y1], [x2, y2]] = st, [[u0, v0], [u1, v1], [u2, v2]] = dt;
        const den = x0 * (y1 - y2) + x1 * (y2 - y0) + x2 * (y0 - y1);
        const a = (u0 * (y1 - y2) + u1 * (y2 - y0) + u2 * (y0 - y1)) / den, bb = (v0 * (y1 - y2) + v1 * (y2 - y0) + v2 * (y0 - y1)) / den;
        const c = (u0 * (x2 - x1) + u1 * (x0 - x2) + u2 * (x1 - x0)) / den, dd = (v0 * (x2 - x1) + v1 * (x0 - x2) + v2 * (x1 - x0)) / den;
        ctx.transform(a, bb, c, dd, u0 - a * x0 - c * y0, v0 - bb * x0 - dd * y0); ctx.drawImage(img, 0, 0); ctx.restore(); } } }
  const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const subQuad = (q, t0, t1) => [lerp(q[0], q[1], t0), lerp(q[0], q[1], t1), lerp(q[3], q[2], t1), lerp(q[3], q[2], t0)];

  // ---------- blend + tone (NORMATIVE — mirrored in mural_merge) ----------
  // Two-pass quality ladder: the room paints INSTANTLY from the 900w tier, then a
  // background pass rebuilds the identical blend from the 2048w masters at up to
  // 1600 px height and swaps it in — art never stays soft on screen. Same stats,
  // same maths, same recipe: the PRINT side is untouched.
  const COMP_HI = 1600;
  let _comp = null, _ckey = "", _compHi = null, _hiKey = "", _hiBuilding = "", _ownImg = null, _ownSlice = null;
  function localStat(im) { const t = document.createElement("canvas"); t.width = 64; t.height = 64;
    const tx = t.getContext("2d"); tx.drawImage(im, 0, 0, 64, 64);
    const d = tx.getImageData(0, 0, 64, 64).data; const m = [0, 0, 0], v = [0, 0, 0], n = 4096;
    for (let k = 0; k < d.length; k += 4) { m[0] += d[k]; m[1] += d[k + 1]; m[2] += d[k + 2]; }
    m[0] /= n; m[1] /= n; m[2] /= n;
    for (let k = 0; k < d.length; k += 4) { v[0] += (d[k] - m[0]) ** 2; v[1] += (d[k + 1] - m[1]) ** 2; v[2] += (d[k + 2] - m[2]) ** 2; }
    return { m, s: [Math.sqrt(v[0] / n), Math.sqrt(v[1] / n), Math.sqrt(v[2] / n)] }; }
  const _blendKey = () => JSON.stringify([MC.members, MC.mood, MC.tone.b, MC.tone.c, MC.tone.s]);
  // hi-res target scales with the DEVICE: a phone needs ~900px, a retina desktop 1600.
  // Keeps iOS canvas memory well inside Safari's budget (the desktop-fixed 1600 was
  // wasteful on phones and risked silent canvas failures there).
  const hiTarget = () => HERO ? COMP_HI : Math.min(COMP_HI, Math.max(900,
    Math.round(contW() * Math.min(2, window.devicePixelRatio || 1) / Math.max(1, totalW()) * MC.h * 1.4)));
  async function compose() {   // returns the BEST blend available now; upgrades arrive async
    if (!MC.members.length) return null;
    const key = _blendKey();
    if (_compHi && _hiKey === key) return _compHi;
    if (!_comp || _ckey !== key) { _comp = await composeAt(COMP_H, 1, false); _ckey = key; }
    hiKick(key);
    return _comp; }
  function hiKick(key) {   // background 2k pass — chunked so the page never stutters
    if (_hiBuilding === key || (_compHi && _hiKey === key)) return;
    _hiBuilding = key;
    composeAt(hiTarget(), 2, true).then(c => {
      if (_hiBuilding !== key) return;
      _hiBuilding = "";
      if (!c || _blendKey() !== key) return;
      _compHi = c; _hiKey = key; _wkey = "";
      draw(); }).catch(() => { _hiBuilding = ""; }); }
  async function composeAt(Hc, tier, chunked) {
    const mem = MC.members; if (!mem.length) return null;
    const T = MC.tone;
    const imgs = [];
    for (const i of mem) {
      let im = tier === 2 ? await loadImg(API + "/flat2k/" + i + ".jpg") : null;
      if (!im) im = await loadImg(API + "/flat/" + i + ".jpg");
      if (im) imgs.push({ im, i }); }
    if (!imgs.length) return null;
    const st = imgs.map(o => STATS[o.i] || localStat(o.im));
    const gm = [0, 1, 2].map(c => st.reduce((a, s) => a + s.m[c], 0) / st.length);
    const gs = [0, 1, 2].map(c => st.reduce((a, s) => a + s.s[c], 0) / st.length);
    const iw = imgs.map(o => Math.round(o.im.width * Hc / o.im.height));
    const ov = []; for (let k = 1; k < imgs.length; k++) ov.push(Math.round(OVF * Math.min(iw[k - 1], iw[k])));
    const xs = [0]; for (let k = 1; k < imgs.length; k++) xs.push(xs[k - 1] + iw[k - 1] - ov[k - 1]);
    const W = xs[xs.length - 1] + iw[iw.length - 1];
    const cv = document.createElement("canvas"); cv.width = W; cv.height = Hc; const x = cv.getContext("2d");
    if (!x) { DIAG(`canvas ctx null ${W}x${Hc} tier${tier}`); return null; }
    const off = MMOODS[MC.mood] || [0, 0, 0];
    // harmonise strength: gentle for blends, NONE for a single artwork (keeps art true —
    // the old 0.55 read as a "pale filter"). Pinned into the recipe for print parity.
    const useStr = imgs.length > 1 ? 0.3 : 0;
    for (let k = 0; k < imgs.length; k++) {
      const l = document.createElement("canvas"); l.width = iw[k]; l.height = Hc; const lx = l.getContext("2d");
      if (!lx) { DIAG(`layer ctx null ${iw[k]}x${Hc}`); return null; }
      lx.imageSmoothingQuality = "high";
      lx.drawImage(imgs[k].im, 0, 0, iw[k], Hc);
      // fast path: single artwork, natural mood -> pure GPU compositing, no pixel reads
      const noop = useStr === 0 && !off[0] && !off[1] && !off[2];
      if (!noop) try { const d = lx.getImageData(0, 0, iw[k], Hc), p = d.data, s = st[k];
        if (useStr === 0) { if (off[0] || off[1] || off[2])
            for (let q = 0; q < p.length; q += 4) for (let ch = 0; ch < 3; ch++)
              p[q + ch] = Math.max(0, Math.min(255, p[q + ch] + off[ch])); }
        else for (let q = 0; q < p.length; q += 4) for (let ch = 0; ch < 3; ch++) {
          const t = (p[q + ch] - s.m[ch]) / (s.s[ch] || 1) * gs[ch] + gm[ch] + off[ch];
          p[q + ch] = Math.max(0, Math.min(255, p[q + ch] * (1 - useStr) + t * useStr)); }
        lx.putImageData(d, 0, 0); } catch (e) { console.warn("wl mural layer", e); }
      if (chunked) await new Promise(r => setTimeout(r, 0));
      if (k === 0) x.drawImage(l, xs[0], 0);
      else { const m = document.createElement("canvas"); m.width = iw[k]; m.height = Hc; const mx = m.getContext("2d");
        const g = mx.createLinearGradient(0, 0, ov[k - 1], 0);
        g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, "rgba(0,0,0,1)");
        mx.fillStyle = g; mx.fillRect(0, 0, ov[k - 1], Hc);
        mx.fillStyle = "#000"; mx.fillRect(ov[k - 1], 0, iw[k] - ov[k - 1], Hc);
        mx.globalCompositeOperation = "source-in"; mx.drawImage(l, 0, 0);
        x.drawImage(m, xs[k], 0); } }
    if (T.b !== 1 || T.c !== 1 || T.s !== 1) {   // brightness -> contrast -> saturation (same as print)
      const bands = chunked ? 4 : 1, bh = Math.ceil(Hc / bands);
      for (let b0 = 0; b0 < Hc; b0 += bh) {
        const hh = Math.min(bh, Hc - b0);
        try { const d = x.getImageData(0, b0, W, hh), p = d.data;
          for (let q = 0; q < p.length; q += 4) {
            let r = (p[q] * T.b - 127.5) * T.c + 127.5;
            let g2 = (p[q + 1] * T.b - 127.5) * T.c + 127.5;
            let b2 = (p[q + 2] * T.b - 127.5) * T.c + 127.5;
            const gr = (r + g2 + b2) / 3;
            p[q] = Math.max(0, Math.min(255, gr + (r - gr) * T.s));
            p[q + 1] = Math.max(0, Math.min(255, gr + (g2 - gr) * T.s));
            p[q + 2] = Math.max(0, Math.min(255, gr + (b2 - gr) * T.s)); }
          x.putImageData(d, 0, b0); } catch (e) { console.warn("wl tone", e); }
        if (chunked) await new Promise(r => setTimeout(r, 0)); } }
    return { cv, W, H: Hc, tier }; }
  let _win = null, _wkey = "";
  function windowOfCached(comp) {   // window slicing is pure — cache it (big rooms = big canvases)
    const key = [_blendKey(), comp.H, comp.tier, MC.offset, totalW(), MC.h, MC.grid,
                 T3 && T3._walk ? 1 : 0].join("|");
    if (_win && _wkey === key) return _win;
    _win = windowOf(comp); _wkey = key; return _win; }
  function windowOf(comp) {
    const tw = totalW();
    // RETINA-AWARE sizing: the wall texture carries at least as many pixels as the
    // screen will actually show (device px per cm × wall height), never more than the
    // blend itself. Walking gets a higher floor — you stand closer to the art.
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const need = Math.round(contW() * dpr / tw * MC.h);
    const Hpx = Math.min(comp.H, Math.max(HERO ? 1400 : (T3 && T3._walk) ? 1100 : 640, need));
    const s = Hpx / comp.H, sW = Math.round(comp.W * s);   // blend size in window space
    const winW = Math.round(Hpx * tw / MC.h);
    if (winW > sW) {
      // blend narrower than the wall run: show it at TRUE size on the first metres of
      // wall (never stretched) and leave the rest bare wall — the room never goes blank
      const cv = document.createElement("canvas"); cv.width = winW; cv.height = Hpx;
      const c2 = cv.getContext("2d");
      c2.fillStyle = "#efece4"; c2.fillRect(0, 0, winW, Hpx);   // matches the room plaster —
      c2.imageSmoothingQuality = "high"; c2.drawImage(comp.cv, 0, 0, sW, Hpx);   // no fake corner
      return { cv, winW, x0: 0, short: true, coverCm: Math.round(comp.W / comp.H * MC.h) };
    }
    const x0 = Math.round(MC.offset * (sW - winW));
    const cv = document.createElement("canvas"); cv.width = winW; cv.height = Hpx;
    const c2 = cv.getContext("2d"); c2.imageSmoothingQuality = "high";
    c2.drawImage(comp.cv, x0 / s, 0, winW / s, comp.H, 0, 0, winW, Hpx);
    return { cv, winW, x0, short: false }; }
  function wallCuts(wl) {
    if (Array.isArray(wl.cuts)) return wl.cuts.filter(c => c > 1 && c < wl.w - 1).sort((a, b) => a - b);
    if (MC.panel === "one" || !(+MC.panel > 0)) return [];
    const out = []; for (let x = +MC.panel; x < wl.w - 1; x += +MC.panel) out.push(Math.round(x * 10) / 10);
    return out; }
  const panelCount = () => MC.walls.reduce((a, wl) => a + wallCuts(wl).length + 1, 0);
  // texture for wall k — the wall's FULL slice of the blend (the photo wall shows the
  // whole wall edge-to-edge, so corners stay continuous whatever the wall's length)
  function wallTexture(win, k, bare) {   // bare = mural + cut lines only (3D room draws openings as geometry)
    const startCm = MC.walls.slice(0, k).reduce((a, w) => a + w.w, 0);
    const wl = MC.walls[k];
    const useCm = wl.w;
    const ppcw = win.cv.width / totalW();
    const sx = Math.round(startCm * ppcw), sw = Math.max(2, Math.round(useCm * ppcw));
    const t = document.createElement("canvas"); t.width = sw; t.height = win.cv.height;
    const x = t.getContext("2d"); x.imageSmoothingQuality = "high";
    x.drawImage(win.cv, sx, 0, sw, win.cv.height, 0, 0, sw, win.cv.height);
    const ppc = sw / useCm, ppcY = win.cv.height / MC.h;
    if (MC.grid) for (const c of wallCuts(wl)) { if (c > useCm) continue;
      const px = Math.round(c * ppc) + .5;
      x.strokeStyle = "rgba(255,255,255,.45)"; x.setLineDash([12, 9]); x.lineWidth = 1.5;
      x.beginPath(); x.moveTo(px, 0); x.lineTo(px, t.height); x.stroke(); x.setLineDash([]); }
    for (const co of (bare ? [] : wl.cutouts || [])) { if (co.x > useCm) continue;
      const rx = co.x * ppc, ry = (MC.h - co.y - co.h) * ppcY, rw = co.w * ppc, rh = co.h * ppcY;
      // realistic openings, drawn to scale (frame widths in real cm)
      x.save();
      x.shadowColor = "rgba(0,0,0,.35)"; x.shadowBlur = 6;
      x.fillStyle = "#f4f2ec"; x.fillRect(rx, ry, rw, rh);   // architrave
      x.restore();
      if (co.kind === "window") {
        const fr = 7 * ppc;
        const gx = rx + fr, gy = ry + fr, gw = rw - 2 * fr, gh = rh - 2 * fr;
        const sky = x.createLinearGradient(0, gy, 0, gy + gh);
        sky.addColorStop(0, "#c9d6dd"); sky.addColorStop(.6, "#dfe6e3"); sky.addColorStop(1, "#eef0e9");
        x.fillStyle = sky; x.fillRect(gx, gy, gw, gh);
        x.save(); x.globalAlpha = .35; x.fillStyle = "#ffffff";   // light streak on the glass
        x.beginPath(); x.moveTo(gx + gw * .15, gy); x.lineTo(gx + gw * .4, gy); x.lineTo(gx + gw * .1, gy + gh); x.lineTo(gx, gy + gh * .8); x.closePath(); x.fill(); x.restore();
        x.fillStyle = "#f4f2ec";                                   // mullions
        x.fillRect(gx + gw / 2 - 2.5 * ppc, gy, 5 * ppc, gh);
        x.fillRect(gx, gy + gh / 2 - 2.5 * ppc, gw, 5 * ppc);
        x.strokeStyle = "rgba(0,0,0,.25)"; x.lineWidth = 1.5; x.strokeRect(gx, gy, gw, gh);
      } else {
        const fr = 6 * ppc;
        x.fillStyle = "#f8f6f1"; x.fillRect(rx + fr, ry + fr, rw - 2 * fr, rh - fr);   // door slab
        const px2 = rx + fr + 9 * ppc, pw2 = rw - 2 * fr - 18 * ppc;
        const p1h = (co.h * .38) * ppcY, p2h = (co.h * .34) * ppcY;
        x.strokeStyle = "rgba(0,0,0,.18)"; x.lineWidth = 2;
        x.strokeRect(px2, ry + fr + 12 * ppcY, pw2, p1h);          // recessed panels
        x.strokeRect(px2, ry + fr + (co.h * .5 + 6) * ppcY, pw2, p2h);
        x.fillStyle = "#6f6a5f";                                    // handle at 100 cm
        x.beginPath(); x.arc(rx + rw - fr - 7 * ppc, ry + (MC.h - co.y - 100) * ppcY, Math.max(2, 2.2 * ppc), 0, 7); x.fill();
      }
      x.strokeStyle = "rgba(0,0,0,.28)"; x.lineWidth = 2; x.strokeRect(rx + 1, ry + 1, rw - 2, rh - 2);
      x.fillStyle = GREEN; x.fillRect(rx + rw - 8, ry + rh - 8, 8, 8);   // resize grip
      const rr = Math.max(9, 7 * ppc);   // ✕ remove chip, top-right (also removable in the toolbar)
      x.fillStyle = "rgba(27,27,27,.85)"; x.beginPath(); x.arc(rx + rw, Math.max(ry, rr), rr, 0, 7); x.fill();
      const ccy = Math.max(ry, rr);
      x.strokeStyle = "#fff"; x.lineWidth = Math.max(1.5, rr * .16);
      x.beginPath(); x.moveTo(rx + rw - rr * .38, ccy - rr * .38); x.lineTo(rx + rw + rr * .38, ccy + rr * .38);
      x.moveTo(rx + rw + rr * .38, ccy - rr * .38); x.lineTo(rx + rw - rr * .38, ccy + rr * .38); x.stroke();
    }
    return { t, useCm }; }

  // ---------- markup ----------
  const ICON = {
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 11v6"/><circle cx="12" cy="7.5" r=".5" fill="#1b1b1b"/></svg>',
    save: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><path d="M12 3v12M7 10l5 5 5-5"/><path d="M4 19h16"/></svg>',
    email: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 7l9 6 9-6"/></svg>',
    share: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><circle cx="6" cy="12" r="2.5"/><circle cx="17" cy="5.5" r="2.5"/><circle cx="17" cy="18.5" r="2.5"/><path d="M8.3 10.8l6.4-4M8.3 13.2l6.4 4"/></svg>',
    zoom: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M16 16l5 5M8 11h6M11 8v6"/></svg>',
    walk: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><circle cx="13" cy="4.5" r="1.8"/><path d="M13 7l-2.6 5 2.8 2.6V20M10.4 12l-2 2.2M13.2 14.6 15.6 20M13 7l3 2 2.4 2.6"/></svg>',
    vr: '<svg viewBox="0 0 24 24" fill="none" stroke="#1b1b1b" stroke-width="2"><rect x="2.5" y="8" width="19" height="9" rx="2.5"/><circle cx="8" cy="12.5" r="1.6"/><circle cx="16" cy="12.5" r="1.6"/><path d="M10.5 15.5c.5-1 2.5-1 3 0"/></svg>' };
  const CSS = `
  #wlm{--ink:#1b1b1b;--soft:#63635f;--paper:#fafafa;--panel:#f5f5f4;--line:#e4e4e2;--acc:${GREEN};--lab:#6a6a66;
    max-width:1280px;margin:0 auto;padding:6px 20px 56px;font-size:14px;color:var(--ink);
    -webkit-text-size-adjust:100%;text-size-adjust:100%}
  #wlm input,#wlm select,#wlm button{-webkit-text-size-adjust:100%;text-size-adjust:100%}
  #wlm *{box-sizing:border-box}
  #wlm button{transition:background .13s ease,border-color .13s ease,color .13s ease,transform .13s ease,box-shadow .13s ease}
  #wlm button:focus-visible,#wlm input:focus-visible,#wlm select:focus-visible{outline:2px solid var(--acc);outline-offset:1px}
  #wlm .wlm-lede{color:var(--soft);margin:10px 0 12px;font-size:.98em;line-height:1.55;max-width:880px}
  #wlm .wlm-zone{padding:18px;border:1px solid var(--line);background:var(--paper)}
  #wlm .wlm-z1{background:var(--panel)}
  #wlm .wlm-z2{background:var(--paper)}
  #wlm h3.wlm-h{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--lab);margin:18px 0 8px;font-weight:600}
  #wlm h3.wlm-h:first-child{margin-top:0}
  /* ---- the STAGE: cinematic room with floating glass controls ---- */
  #wlm .wlm-wall{position:relative;background:#ececeb;border:1px solid var(--line);overflow:hidden;
    box-shadow:0 1px 2px rgba(27,27,27,.05)}
  #wlm canvas{display:block;width:100%;height:auto}
  #wlm .wlm-tabs{position:absolute;top:12px;left:12px;right:12px;z-index:7;display:flex;flex-wrap:wrap;align-items:center;gap:8px;pointer-events:none}
  #wlm .wlm-tabs>*{pointer-events:auto}
  #wlm .wlm-tabs .wt{border:1px solid rgba(27,27,27,.14);background:rgba(255,255,255,.86);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
    color:var(--ink);padding:8px 15px;cursor:pointer;font-family:inherit;font-size:12.5px;}
  #wlm .wlm-tabs .wt:hover{background:#fff;border-color:rgba(27,27,27,.3)}
  #wlm .wlm-tabs .wt.on{background:var(--ink);color:#fff;border-color:var(--ink)}
  #wlm .wlm-tabs .fv{border:1px solid rgba(27,27,27,.14);background:rgba(255,255,255,.86);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
    width:36px;height:36px;padding:7px;cursor:pointer;}
  #wlm .wlm-tabs .fv:hover{background:#fff;transform:translateY(-1px);box-shadow:0 3px 10px -4px rgba(27,27,27,.35)}
  #wlm .wlm-tabs .fv svg{width:100%;height:100%}
  #wlm .wlm-tabs .fv.on{background:var(--ink);border-color:var(--ink)}
  #wlm .wlm-tabs .fv.on svg *{stroke:#fff}
  #wlm .wlm-joy{position:absolute;left:18px;bottom:18px;width:96px;height:96px;border-radius:50%;background:rgba(27,27,27,.18);
    backdrop-filter:blur(3px);display:none;touch-action:none;z-index:8;border:1px solid rgba(255,255,255,.35)}
  #wlm .wlm-joy .nub{position:absolute;left:50%;top:50%;width:42px;height:42px;border-radius:50%;background:rgba(255,255,255,.92);
    transform:translate(-50%,-50%);box-shadow:0 2px 8px rgba(0,0,0,.4);pointer-events:none}
  #wlm .wlm-zoomers{position:absolute;right:14px;bottom:14px;z-index:7;display:none;flex-direction:column;gap:6px}
  #wlm .wlm-zoomers button{width:36px;height:36px;border:1px solid rgba(27,27,27,.14);
    background:rgba(255,255,255,.86);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
    font-size:17px;line-height:1;cursor:pointer;color:var(--ink);font-family:inherit}
  #wlm .wlm-zoomers button:hover{background:#fff;transform:translateY(-1px)}
  #wlm .wlm-fld{display:flex;flex-direction:column;gap:3px;font-size:11px;letter-spacing:.04em;color:var(--soft)}
  #wlm .wlm-fld input{width:72px;padding:7px 8px}
  #wlm .wlm-fld{min-width:0}
  #wlm .wlm-fld .u{font-size:11px;color:#8a8578}
  #wlm .wlm-dockcard{background:var(--paper);border:1px solid var(--line);padding:14px 16px;margin-top:10px}
  #wlm .wlm-dockcard h3.wlm-h{margin-top:0}
  #wlm #wlmFiltW{display:none;padding:10px 0 2px}
  #wlm .wlm-seltool{display:none;position:absolute;left:12px;bottom:12px;z-index:6;max-width:calc(100% - 24px);
    gap:8px;align-items:center;flex-wrap:wrap;background:rgba(255,255,255,.9);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
    border:1px solid rgba(27,27,27,.12);padding:8px 12px;font-size:12.5px;box-shadow:0 6px 22px -12px rgba(27,27,27,.4)}
  #wlm .wlm-guide{display:none;background:#fff;border:1px solid var(--acc);margin-top:8px;padding:9px 13px;font-size:12.5px;justify-content:space-between;align-items:center;gap:12px}
  /* ---- status line + dock under the stage ---- */
  #wlm #wlmConfirm{margin:10px 2px 0;font-size:12.5px}
  #wlm .wlm-dock{display:flex;flex-wrap:wrap;gap:10px 18px;align-items:center;margin-top:10px;
    background:var(--paper);border:1px solid var(--line);padding:10px 14px}
  #wlm .wlm-order{display:none;gap:8px;align-items:center;flex-wrap:wrap}
  #wlm .wlm-order .oc{position:relative;width:56px;height:64px;flex:none;cursor:grab;border:2px solid #fff;outline:1px solid var(--line);
    border-radius:2px;touch-action:none;transition:outline-color .13s ease,transform .13s ease}
  #wlm .wlm-order .oc:hover{outline-color:var(--ink);transform:translateY(-1px)}
  #wlm .wlm-order .oc img{width:100%;height:100%;object-fit:cover;pointer-events:none;border-radius:1px}
  #wlm .wlm-order .oc .x{position:absolute;top:-7px;right:-7px;width:19px;height:19px;border-radius:50%;background:var(--ink);color:#fff;font-size:11px;line-height:19px;text-align:center;cursor:pointer}
  #wlm .wlm-order .oc .x:hover{background:#a33320}
  #wlm .wlm-order .oc .rr2{position:absolute;bottom:-7px;left:-7px;width:19px;height:19px;border-radius:50%;background:var(--acc);color:#fff;font-size:11px;line-height:19px;text-align:center;cursor:pointer;opacity:0;transition:opacity .13s ease}
  #wlm .wlm-order .oc:hover .rr2{opacity:1}
  @media(hover:none){#wlm .wlm-order .oc .rr2{opacity:1}}
  #wlm .wlm-order .oc.dragging{opacity:.55;outline:2px solid var(--acc)}
  #wlm .wlm-note{font-size:12.5px;color:var(--soft);margin:6px 0 0;line-height:1.5}
  #wlm .wlm-warn{color:#a33320;font-size:12.5px;margin:6px 0 0;line-height:1.5}
  #wlm .wlm-chip{border:1px solid var(--line);background:#fff;padding:6px 12px;cursor:pointer;font-family:inherit;font-size:12px;margin:0 5px 5px 0;color:var(--ink);}
  #wlm .wlm-chip:hover{border-color:var(--ink)}
  #wlm .wlm-chip.on{background:var(--ink);color:#fff;border-color:var(--ink)}
  #wlm .wlm-chip:disabled{opacity:.4;cursor:default}
  #wlm input[type=text],#wlm input[type=number],#wlm input[type=email],#wlm select{border:1px solid #cbc6bb;padding:8px 10px;font-family:inherit;font-size:13px;background:#fff;color:var(--ink);transition:border-color .13s ease}
  #wlm input:hover,#wlm select:hover{border-color:var(--ink)}
  #wlm .wlm-num{width:74px}
  #wlm input[type=range]{width:120px;vertical-align:middle;accent-color:var(--acc)}
  #wlm .wlm-cols{display:grid;grid-template-columns:1.08fr .92fr;gap:18px;align-items:start;margin-top:16px}
  #wlm .wlm-cols>*{min-width:0;margin-top:0}
  #wlm .wlm-mini{border:1px solid var(--line);background:#fff;cursor:pointer;padding:4px 10px;font-size:12px;font-family:inherit;color:var(--ink);}
  #wlm .wlm-mini:hover{border-color:var(--ink)}
  #wlm .wlm-acc{border-top:1px solid var(--line);margin-top:12px}
  #wlm .wlm-acc summary{display:flex;justify-content:space-between;align-items:center;gap:10px;cursor:pointer;list-style:none;padding:12px 0 6px}
  #wlm .wlm-acc summary::-webkit-details-marker{display:none}
  #wlm .wlm-acc summary .t{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--lab);font-weight:600}
  #wlm .wlm-acc summary .v{font-size:12px;color:var(--soft);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  #wlm .wlm-acc summary .v::after{content:" ▾";color:#9a958c}
  #wlm .wlm-acc[open] summary .v::after{content:" ▴"}
  #wlm #wlmThemePills{display:none}
  #wlm .wlm-mat{display:inline-flex;align-items:center;gap:8px;padding:5px 12px 5px 6px}
  #wlm .wlm-mat img{width:34px;height:34px;object-fit:cover;border-radius:2px;display:block}
  #wlm .wlm-egrow{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin:10px 2px 0;font-size:12.5px}
  #wlm .wlm-egrow .how{color:var(--soft);margin-left:auto}
  /* ---- buy console ---- */
  #wlm .wlm-buy{background:#fff;border:1px solid var(--line);padding:14px 16px;margin:0 0 14px;position:sticky;top:12px;z-index:20}
  #wlm .wlm-title{font-size:1.7em;font-weight:600;letter-spacing:.01em;margin:8px 0 4px}
  #wlm .wlm-price{font-size:1.65em;font-weight:600;letter-spacing:.01em}
  #wlm .wlm-strike{text-decoration:line-through;color:#9a958c;font-weight:400;margin-right:8px;font-size:.72em}
  #wlm .wlm-atc{display:block;width:100%;background:var(--acc);border:1px solid var(--acc);color:#fff;padding:15px;letter-spacing:.14em;text-transform:uppercase;font-size:12px;cursor:pointer;font-family:inherit;margin-top:12px;}
  #wlm .wlm-atc:hover:not(:disabled){background:#3c4427;box-shadow:0 6px 18px -8px rgba(74,82,48,.6)}
  #wlm .wlm-atc:disabled{opacity:.45;cursor:default}
  #wlm .wlm-btn{background:var(--acc);border:1px solid var(--acc);color:#fff;padding:10px 18px;cursor:pointer;font-family:inherit;font-size:12px;letter-spacing:.06em;}
  #wlm .wlm-btn:hover{background:#3c4427}
  #wlm .wlm-srch{display:flex;gap:7px;margin:8px 0;flex-wrap:wrap}
  #wlm .wlm-srch input{flex:1 1 200px;min-width:0}
  #wlm .wlm-srch .go{background:var(--acc);color:#fff;border:1px solid var(--acc);padding:0 16px;cursor:pointer;font-family:inherit;font-size:11px;letter-spacing:.12em;text-transform:uppercase}
  #wlm .wlm-srch .go:hover{background:#3c4427}
  #wlm .wlm-filters{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin:0 0 10px}
  #wlm .wlm-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(132px,1fr));gap:14px}
  #wlm .wlm-card{cursor:pointer;position:relative;transition:transform .13s ease}
  #wlm .wlm-card:hover{transform:translateY(-2px)}
  #wlm .wlm-card .im{background:linear-gradient(100deg,#f1efe9 40%,#faf9f5 50%,#f1efe9 60%);background-size:200% 100%;
    animation:wlmShimmer 1.4s linear infinite;border:1px solid var(--line);height:128px;display:flex;align-items:center;justify-content:center;overflow:hidden}
  #wlm .wlm-card img{max-width:100%;max-height:100%;display:block}
  #wlm .wlm-card:hover .im{box-shadow:0 8px 20px -10px rgba(27,27,27,.35);border-color:#cbc6bb}
  #wlm .wlm-card.on .im{outline:3px solid var(--acc);outline-offset:-1px}
  #wlm .wlm-card .ck{position:absolute;top:7px;right:7px;width:23px;height:23px;border-radius:50%;background:rgba(27,27,27,.28);color:#fff;font-size:12px;line-height:23px;text-align:center;transition:background .13s ease}
  #wlm .wlm-card:hover .ck{background:var(--ink)}
  #wlm .wlm-card.on .ck{background:var(--acc)}
  #wlm .wlm-card .t{font-size:11.5px;margin-top:5px;text-align:center;line-height:1.3}
  #wlm .wlm-card .a{font-size:9.5px;color:#8a8578;text-align:center;letter-spacing:.04em}
  @keyframes wlmShimmer{to{background-position:-200% 0}}
  #wlm .wlm-pager{display:flex;gap:5px;justify-content:center;align-items:center;margin-top:14px;flex-wrap:wrap}
  #wlm .wlm-pager button{border:1px solid var(--line);background:#fff;min-width:31px;height:31px;cursor:pointer;font-family:inherit;font-size:12px;color:var(--ink)}
  #wlm .wlm-pager button:hover:not(:disabled){border-color:var(--ink)}
  #wlm .wlm-pager button.on{background:var(--ink);color:#fff;border-color:var(--ink)}
  #wlm .wlm-pager button:disabled{opacity:.3;cursor:default}
  #wlm .wlm-share{display:none;margin-top:8px;gap:7px}
  #wlm .wlm-share input{flex:1}
  #wlm .wlm-upnl{background:#fff;border:1px solid var(--line);padding:26px 20px;margin-top:8px;text-align:center}
  #wlm .wlm-ruler{position:relative;height:36px;background:#fff;border:1px solid var(--line);margin:4px 0 10px}
  #wlm .wlm-ruler .rl{position:absolute;left:6px;top:2px;font-size:10px;color:#8a8578;pointer-events:none}
  #wlm .wlm-ruler .rr{position:absolute;right:6px;top:2px;font-size:10px;color:#8a8578;pointer-events:none}
  #wlm .wlm-ruler .mk{position:absolute;top:0;width:18px;height:100%;margin-left:-9px;cursor:col-resize;touch-action:none}
  #wlm .wlm-ruler .mk::before{content:"";position:absolute;left:8px;top:0;width:2px;height:100%;background:var(--acc)}
  #wlm .wlm-ruler .mk span{position:absolute;bottom:1px;left:50%;transform:translateX(-50%);font-size:9.5px;background:var(--acc);color:#fff;padding:0 4px;white-space:nowrap;border-radius:2px}
  /* ---- PHONE density layer (Summer 2026-07-20: "too spaced out, not usable") ----
     Mobile is its own layout: tiny chrome over the room, hint text off (the one-time
     toast + ⓘ guide teach it), tighter rhythm everywhere, 3-up art grid, slim bar. */
  @media(max-width:640px){
    #wlm{padding:2px 12px 84px}
    #wlm .wlm-title{font-size:1.32em;margin:6px 0 2px}
    #wlm .wlm-lede{font-size:12.5px;line-height:1.45;margin:5px 0 8px}
    #wlm .wlm-tabs{top:8px;left:8px;right:8px;gap:5px}
    #wlm .wlm-tabs .wt{padding:6px 10px;font-size:11px}
    #wlm .wlm-tabs .fv{width:29px;height:29px;padding:5px}
    #wlm .wlm-zoomers{right:10px;bottom:10px;gap:5px}
    #wlm .wlm-zoomers button{width:31px;height:31px;font-size:15px}
    #wlm .wlm-seltool{left:8px;bottom:8px;padding:6px 9px;font-size:11.5px;gap:6px;max-width:calc(100% - 58px)}
    #wlm .wlm-seltool .wlm-note{display:none}
    #wlm #wlmConfirm{font-size:11.5px;margin:8px 2px 0}
    #wlm .wlm-egrow{margin:8px 0 0;gap:6px;font-size:12px;flex-wrap:nowrap;overflow-x:auto;-webkit-overflow-scrolling:touch}
    #wlm .wlm-egrow>*{flex:none}
    #wlm .wlm-egrow .how{display:none}
    #wlm .wlm-zone{padding:12px}
    #wlm h3.wlm-h{margin:12px 0 6px;font-size:10px}
    #wlm .wlm-chip{padding:5px 10px;font-size:11.5px;margin:0 4px 4px 0}
    #wlm .wlm-mini{padding:4px 8px;font-size:11px}
    #wlm .wlm-order .oc{width:44px;height:50px}
    #wlm .wlm-srch{gap:6px}
    #wlm .wlm-srch input{flex-basis:100%}
    #wlm .wlm-cards{display:grid;grid-auto-flow:column;grid-auto-columns:31%;
      overflow-x:auto;-webkit-overflow-scrolling:touch;gap:8px;scroll-snap-type:x proximity;padding-bottom:4px}
    #wlm .wlm-card{scroll-snap-align:start}
    #wlm #wlmTheme,#wlm #wlmSaveTheme{display:none}
    #wlm #wlmThemePills{display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;gap:6px;padding:6px 0 2px}
    #wlm #wlmThemePills .wlm-chip{flex:none;margin:0}
    #wlm .wlm-filters select{width:100%;max-width:none}
    #wlm .wlm-note{font-size:12px}
    #wlm .wlm-acc summary{padding:11px 0 5px}
    #wlm .wlm-card .im{height:96px}
    #wlm .wlm-card .t{font-size:10px;margin-top:3px}
    #wlm .wlm-card .a{font-size:8.5px}
    #wlm .wlm-buy{padding:8px 10px;gap:8px}
    #wlm .wlm-buy .wlm-price{font-size:1.1em}
    #wlm .wlm-atc{padding:12px;font-size:11px;letter-spacing:.1em}
    #wlm .wlm-fld input{width:58px;padding:6px 6px}
    #wlm #wlmWallsDock{padding:10px 12px;margin-top:8px}
    #wlm #wlmEmailI{display:none}
    #wlm .wlm-pager{display:none}
    #wlm input[type=range]{width:86px}
    #wlm .wlm-mat img{width:26px;height:26px}
    #wlm input[type=text],#wlm input[type=email],#wlm select{font-size:13px}
    #wlm #wlmTheme{max-width:none;flex:1 1 130px;min-width:0}
    #wlm .wlm-order{align-items:flex-start}
    #wlm .wlm-order>.wlm-note{flex-basis:100%}
  }
  @media(max-width:899px){
    #wlm{padding-bottom:96px}
    #wlm .wlm-cols{grid-template-columns:1fr}
    /* top:auto is LOAD-BEARING (P0 2026-07-20): the desktop sticky rule sets top:12px —
       without the reset this fixed bar stretches top-to-bottom into a white sheet that
       covers the ENTIRE page ("price but nothing else" on phones). */
    #wlm .wlm-buy{position:fixed;left:0;right:0;bottom:0;top:auto;z-index:900;margin:0;border-radius:0;border-left:0;border-right:0;border-bottom:0;
      box-shadow:0 -8px 28px -14px rgba(27,27,27,.4);display:flex;align-items:center;gap:12px;padding:10px 14px}
    #wlm .wlm-buy .wlm-price{font-size:1.25em;flex:none}
    #wlm .wlm-buy #wlmDeal{display:none}
    #wlm .wlm-atc{margin-top:0;flex:1;padding:13px}
  }
  /* ---- Guided steps (Composed Walls 2026-08-13, tabbed layout Summer 2026-08-19).
     The room preview stays on top; below it a TAB bar (1 Define your wall ·
     2 Select the art · 3 Make it yours); the active tab's controls render in a
     bounded pane; price + Add to cart close the section — always LAST.
     A visibility wrapper ONLY: controls are hidden per tab, never removed —
     refresh()/draw() need every id. No transform/filter on any wrapper
     (would break the fixed mobile buy bar). */
  #wlm .wlm-steps{display:none}
  #wlm.wlm-guided .wlm-steps{display:flex;gap:0;align-items:stretch;margin:16px 0 0;border-bottom:2px solid #e2ded6}
  #wlm .wlm-steps .st{border:0;border-bottom:2px solid transparent;margin-bottom:-2px;background:none;
    padding:11px 18px;cursor:pointer;font-weight:600;font-size:.82em;letter-spacing:.06em;
    text-transform:uppercase;color:#6b6b66;border-radius:0}
  #wlm .wlm-steps .st.on{border-bottom-color:${GREEN};color:${GREEN}}
  #wlm .wlm-steps .st.done{color:#3a3a36}
  #wlm .wlm-stephint{display:none}
  #wlm.wlm-guided .wlm-stephint{display:block;margin:10px 2px 12px;color:#6b6b66;font-size:.9em}
  #wlm .wlm-stepnav{display:none}
  #wlm.wlm-gok .wlm-stepnav{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:14px 0 2px}
  #wlm .wlm-doors{display:none;gap:8px;flex-wrap:wrap;margin:2px 0 10px}
  #wlm.wlm-guided .wlm-doors{display:flex}
  #wlm.wlm-guided .wlm-cols{grid-template-columns:1fr;margin-top:0}
  /* the active tab's controls sit in one bounded pane, not the whole screen */
  #wlm.wlm-guided .wlm-zone,#wlm.wlm-guided #wlmWallsDock{border:1.5px solid #e2ded6;border-top:0;
    background:#fff;padding:16px 18px;margin-top:0}
  #wlm.wlm-guided.wlm-s1 .wlm-cols,#wlm.wlm-guided.wlm-s1 .wlm-egrow{display:none}
  #wlm.wlm-guided.wlm-s2 #wlmWallsDock{display:none}
  #wlm.wlm-guided.wlm-s2 .wlm-z1{display:none}
  #wlm.wlm-guided.wlm-s3 .wlm-cols>.wlm-z2{display:none}
  #wlm.wlm-guided.wlm-s3 .wlm-egrow,#wlm.wlm-guided.wlm-s3 #wlmWallsDock{display:none}
  /* buy console closes the section on desktop; the phone keeps its fixed bottom bar */
  @media(min-width:900px){
    #wlm .wlm-buy{position:static;top:auto;margin-top:16px;border-top:2px solid #e2ded6;padding-top:16px}
  }
  `;

  root.innerHTML = `<style>${CSS}</style><div id="wlm">
   <h1 class="wlm-title">Design your wall mural</h1>
   <p class="wlm-lede">One continuous artwork, composed from real masterpieces and printed to your exact wall. Enter your sizes, choose the art — the wall you approve on screen is the wall we print.</p>

   <div class="wlm-wall">
    <div class="wlm-tabs" id="wlmTabs"></div>
    <canvas id="wlmCv" width="1200" height="500"></canvas>
    <div class="wlm-joy" id="wlmJoy"><div class="nub" id="wlmJoyN"></div></div>
    <div class="wlm-zoomers" id="wlmZoomers"><button id="wlmZin" title="Zoom in">＋</button><button id="wlmZout" title="Zoom out">−</button></div>
   </div>
   <div class="wlm-guide" id="wlmGuide"><span>Drag the 4 dots to the corners of your wall, then press Done.</span>
    <button class="wlm-btn" id="wlmDone" style="padding:6px 16px">Done</button></div>
   <p class="wlm-note" id="wlmConfirm"></p>
   <div class="wlm-steps" id="wlmSteps"></div>
   <div class="wlm-stephint" id="wlmStepHint"></div>
   <div class="wlm-dockcard" id="wlmWallsDock">
    <div id="wlmWalls" style="display:flex;gap:10px 14px;flex-wrap:wrap;align-items:end"></div>
   </div>
   <div id="wlmUp"></div>
   <div id="wlmAi"></div>
   <div class="wlm-share" id="wlmShareRow"><input type="text" id="wlmShareL" readonly><button class="wlm-mini" id="wlmShareC">Copy</button></div>
   <div class="wlm-share" id="wlmEmailRow"><input type="email" id="wlmEmailIn" placeholder="Your email"><button class="wlm-mini" id="wlmEmailGo">Send</button></div>

   <div class="wlm-cols">
    <div class="wlm-zone wlm-z2">
     <h3 class="wlm-h">Choose the art</h3>
     <div class="wlm-doors" id="wlmDoors"></div>
     <div class="wlm-egrow" id="wlmEg"></div>
     <div class="wlm-order" id="wlmOrder"></div>
     <div style="display:flex;gap:7px;flex-wrap:wrap;align-items:center;margin-top:6px">
      <button class="wlm-btn" id="wlmAuto" style="padding:9px 14px" title="fill the walls with a matched set in one tap">✨ Design it for me</button>
      <select id="wlmTheme" style="max-width:230px" title="curated colour themes"></select>
      <button class="wlm-mini" id="wlmSaveTheme" title="save the artworks on the wall as your own theme">＋ Save as my theme</button>
     </div>
     <div id="wlmThemePills"></div>
     <div class="wlm-srch">
      <input type="text" id="wlmQ" placeholder="Search the collection — describe what belongs on your wall…">
      <button class="wlm-mini" id="wlmCam" title="Search with a photo — of a room, a colour, anything" style="padding:0 12px">📷</button>
      <button class="go" id="wlmGo">Search</button>
      <button class="wlm-mini" id="wlmClr" title="Clear the search" style="padding:0 12px">✕</button>
      <button class="wlm-mini" id="wlmFiltT" title="Artist, style and colour filters">Filters ▾</button>
     </div>
     <div id="wlmFiltW"><div class="wlm-filters" id="wlmFilters"></div></div>
     <div class="wlm-note" id="wlmEcho" style="min-height:16px;margin:0 0 8px"></div>
     <div class="wlm-cards" id="wlmCards"></div>
     <div class="wlm-pager" id="wlmPager"></div>
    </div>
    <div class="wlm-zone wlm-z1">
     <details class="wlm-acc"><summary><span class="t">Panels &amp; cutting</span><span class="v" id="wlmAccPanV"></span></summary>
      <div id="wlmPanels"></div>
      <div id="wlmRulers"></div>
      <label class="wlm-note" style="display:block"><input type="checkbox" id="wlmGridT"> show the cut lines on the room preview</label>
      <div class="wlm-note" id="wlmPanelN"></div>
     </details>
     <details class="wlm-acc"><summary><span class="t">Colour &amp; mood</span><span class="v" id="wlmAccColV"></span></summary>
      <div id="wlmMoods"></div>
      <div class="wlm-note" style="margin-top:5px">
       Brightness <input type="range" id="wlmTb" min="80" max="120" step="1"> <span id="wlmTbV"></span> ·
       Contrast <input type="range" id="wlmTc" min="80" max="120" step="1"> <span id="wlmTcV"></span> ·
       Colour <input type="range" id="wlmTs" min="60" max="140" step="1"> <span id="wlmTsV"></span>
       <button class="wlm-mini" id="wlmTreset">reset</button></div>
     </details>
     <details class="wlm-acc"><summary><span class="t">Material</span><span class="v" id="wlmAccMatV"></span></summary>
      <div id="wlmMats"></div>
      <p class="wlm-note" id="wlmMatD"></p>
     </details>
     <p class="wlm-note">Printed as one piece (openings included) and cut to your lines — cutting is free, and every mural carries a 10 cm safety margin you trim on site. Shipped rolled and insured, shipping included — Australia, New Zealand, the US, Canada, the UK, Europe and Singapore. Numbered panels and hanging instructions in the box (no paste included).</p>
    </div>
   </div>
   <div class="wlm-stepnav" id="wlmStepNav"></div>
   <div class="wlm-buy">
    <div class="wlm-price" id="wlmPrice">…</div>
    <div class="wlm-note" id="wlmDeal"></div>
    <button class="wlm-atc" id="wlmAtc">Add mural to cart</button>
   </div></div>`;

  // ---------- walls setup (right column) ----------
  function wallsUI() {
    const n = MC.walls.length;
    // number-driven (Summer 2026-07-20): all four width boxes on ONE line — type a
    // width and the wall exists, clear it and the run shortens. No chips, no checkbox.
    $("#wlmWalls").innerHTML =
      [0, 1, 2, 3].map(k => { const wl = MC.walls[k];
        return `<label class="wlm-fld">Wall ${k + 1}${k === 3 ? " · room" : ""}
          <span style="display:flex;gap:3px;align-items:center"><input type="number" data-ww="${k}" value="${wl ? wl.w : ""}" placeholder="—" min="100" max="${MAX_WALL}" step="10"><span class="u">cm</span></span></label>`; }).join("") +
      `<label class="wlm-fld">Ceiling
        <span style="display:flex;gap:3px;align-items:center"><input type="number" id="wlmH" value="${MC.h}" min="100" max="${MAX_H}" step="5"><span class="u">cm</span></span></label>
       <span class="wlm-note" style="margin:0 0 8px" id="wlmTot"></span>
       <span style="display:flex;gap:6px;align-self:end;padding-bottom:4px">
        <button class="wlm-mini" id="wlmAddWin">+ window</button>
        <button class="wlm-mini" id="wlmAddDoor">+ door</button></span>`;
    const midW = () => Math.floor((MC.walls.length - 1) / 2);
    $("#wlmAddWin").onclick = () => addCutout(midW(), "window");
    $("#wlmAddDoor").onclick = () => addCutout(midW(), "door");
    $$("#wlmWalls [data-ww]").forEach(inp => inp.onchange = () => {
      const k = +inp.dataset.ww; let v = Math.round(+inp.value || 0);
      if (!v || v < 100) {                       // cleared (or too small) = remove from here on
        if (k === 0) { toast("Walls start at 100 cm."); inp.value = MC.walls[0].w; return; }
        MC.walls = MC.walls.slice(0, k); MC.sel = Math.min(MC.sel, MC.walls.length - 1);
        persist(); wallsUI(); refresh(); return; }
      if (v > MAX_WALL) { toast("A single wall runs up to 10 m — use the next wall box for more."); v = MAX_WALL; }
      while (MC.walls.length <= k) MC.walls.push({ w: 400, cuts: null, cutouts: [] });
      MC.walls[k].w = v; MC.walls[k].cuts = null;
      MC.walls[k].cutouts = (MC.walls[k].cutouts || []).filter(co => co.x + co.w < v);
      persist(); wallsUI(); refresh(); });
    $("#wlmH").onchange = () => { let v = Math.round(+$("#wlmH").value || 0);
      if (!(v >= 100)) { toast("Height starts at 100 cm."); $("#wlmH").value = MC.h; return; }
      if (v > MAX_H) { v = MAX_H; $("#wlmH").value = v; }
      MC.h = v; MC.walls.forEach(wl => { wl.cutouts = (wl.cutouts || []).filter(co => co.y + co.h < v); });
      const roll = MATS[MC.material] ? MATS[MC.material][2] : 320;
      if (MC.panel === "one" && v > roll) { MC.panel = 100;
        toast("Above " + roll + " cm the mural needs panels — one-piece switched off."); }
      persist(); wallsUI(); ctrlsUI(); refresh(); };
    $("#wlmTot").textContent = `Total ${(totalW() / 100).toFixed(1)} m · ${(totalW() * MC.h / 1e4).toFixed(1)} m²`; }
  function addCutout(k, kind) { const wl = MC.walls[k]; if (!wl) return;
    if ((wl.cutouts || []).length >= 6) { toast("Up to six openings per wall."); return; }
    const co = kind === "door"
      ? { kind, w: Math.min(90, wl.w - 20), h: Math.min(210, MC.h - 10), x: Math.round(wl.w / 2 - 45), y: 0 }
      : { kind, w: Math.min(150, wl.w - 20), h: Math.min(120, MC.h - 40), x: Math.round(wl.w / 2 - 75), y: Math.round(Math.max(10, MC.h - 210)) };
    co.x = Math.max(0, co.x); wl.cutouts = wl.cutouts || [];
    // don't drop it on top of an existing opening — find the nearest free spot
    const clash = () => wl.cutouts.some(o => co.x < o.x + o.w + 12 && o.x < co.x + co.w + 12);
    let guard2 = 0;
    while (clash() && guard2++ < 20) co.x = Math.min(wl.w - co.w, co.x + co.w + 24) === co.x ? 0 : Math.min(wl.w - co.w, co.x + co.w + 24);
    wl.cutouts.push(co);
    persist(); draw();
    toast(`${kind === "door" ? "Door" : "Window"} added to wall ${k + 1} — drag it on the photo; pull its corner to size it.`, 4500); }

  // ---------- cut rulers (right column, millimetre-precise) ----------
  function rulersUI() {
    const el = $("#wlmRulers");
    if (MC.panel === "one") { el.innerHTML = ""; return; }
    el.innerHTML = MC.walls.map((wl, k) => {
      const cuts = wallCuts(wl);
      return `<div class="wlm-note" style="margin:6px 0 0">Wall ${k + 1} — cut positions in mm, drag to adjust</div>
       <div class="wlm-ruler" data-rk="${k}">
        <span class="rl">0</span><span class="rr">${wl.w * 10} mm</span>
        ${cuts.map((c, qi) => `<span class="mk" data-rq="${qi}" style="left:${c / wl.w * 100}%"><span>${Math.round(c * 10)}</span></span>`).join("")}
       </div>`; }).join("");
    $$("#wlmRulers .mk").forEach(mk => {
      mk.onpointerdown = e => { e.preventDefault(); try{mk.setPointerCapture(e.pointerId)}catch(x){}
        const track = mk.parentElement, k = +track.dataset.rk, qi = +mk.dataset.rq;
        const wl = MC.walls[k];
        mk.onpointermove = ev => { const r = track.getBoundingClientRect();
          let c = (ev.clientX - r.left) / r.width * wl.w;
          const cuts = wallCuts(wl).slice();
          const lo = (qi > 0 ? cuts[qi - 1] : 0) + 20, hi = (qi < cuts.length - 1 ? cuts[qi + 1] : wl.w) - 20;
          c = Math.max(lo, Math.min(hi, Math.round(c * 10) / 10));
          cuts[qi] = c; wl.cuts = cuts;
          mk.style.left = (c / wl.w * 100) + "%"; mk.querySelector("span").textContent = Math.round(c * 10);
          clearTimeout(mk._h); mk._h = setTimeout(draw, 120); };
        mk.onpointerup = () => { mk.onpointermove = null; persist(); refresh(); }; }; }); }

  // ---------- tabs / panes ----------
  function tabsUI() { $("#wlmTabs").innerHTML =
    `<button class="wt ${MC.tab === "tpl" ? "on" : ""}" data-t="tpl">Demo room</button>
     <button class="wt ${MC.tab === "own" ? "on" : ""}" data-t="own">My room</button>
     <button class="wt ${MC.tab === "ai" ? "on" : ""}" data-t="ai">AI photo</button>
     ${MC.tab === "own" && MC.ownSrc ? '<button class="wlm-mini" id="wlmRepick">Change photo</button><button class="wlm-mini" id="wlmRedo">Re-do wall corners</button>' : ""}
     <span style="flex:1"></span>
     ${MC.tab === "tpl" ? `<button class="fv" id="wlmWalkB" title="Walk into the room">${ICON.walk}</button>` : ""}
     ${MC.tab === "tpl" && T3 && T3.xrOk ? `<button class="fv" id="wlmVrB" title="See it in your VR headset — true size">${ICON.vr}</button>` : ""}
     <button class="fv" id="wlmZoomI" title="Enlarge">${ICON.zoom}</button>
     <button class="fv" id="wlmInfo" title="How this works">${ICON.info}</button>
     <button class="fv" id="wlmSaveI" title="Save image">${ICON.save}</button>
     <button class="fv" id="wlmEmailI" title="Email it to me">${ICON.email}</button>
     <button class="fv" id="wlmShareI" title="Share this wall">${ICON.share}</button>`;
    $$("#wlmTabs [data-t]").forEach(b => b.onclick = () => setTab(b.dataset.t));
    const rp = $("#wlmRepick"); if (rp) rp.onclick = ownUpload;
    const rd = $("#wlmRedo"); if (rd) rd.onclick = () => { MC.ownEdit = true; guide(true); draw(); };
    $("#wlmZoomI").onclick = zoomOpen; $("#wlmInfo").onclick = infoPopup; $("#wlmSaveI").onclick = saveImage;
    $("#wlmEmailI").onclick = emailRun; $("#wlmShareI").onclick = shareRun;
    const wb = $("#wlmWalkB"); if (wb) { wb.onclick = wkToggle;
      if (T3 && T3._walk) wb.classList.add("on"); }
    const vb = $("#wlmVrB"); if (vb) vb.onclick = vrEnter; }
  function guide(on) { disp("#wlmGuide", on ? "flex" : "none"); }
  function setTab(t) { MC.tab = t; tabsUI(); paneUI(); draw(); }
  function paneUI() {
    const up = $("#wlmUp"), ai = $("#wlmAi");
    up.innerHTML = ""; ai.innerHTML = "";
    if (MC.tab === "own" && !MC.ownSrc)
      up.innerHTML = `<div class="wlm-upnl"><div>See your mural on <b>your own wall</b>.</div>
        <div class="wlm-note" style="margin:4px 0 12px">Upload a photo of your room, then drag the four dots to your wall corners.</div>
        <button class="wlm-btn" id="wlmOwnUp">Upload your room photo</button></div>`;
    if (MC.tab === "ai") {
      if (!MC.ownSrc)
        ai.innerHTML = `<div class="wlm-upnl"><div>A realistic AI photo of this mural in <b>your own room</b>.</div>
          <div class="wlm-note" style="margin:4px 0 12px">Upload a photo of your room to start. Free — five photos each day.</div>
          <button class="wlm-btn" id="wlmAiUp">Upload your room photo</button></div>`;
      else ai.innerHTML = `<div style="margin-top:6px">
          ${MC.aiUrl ? `<img src="${API + MC.aiUrl}" style="max-width:100%;border:1px solid #e6e2dc"><p class="wlm-note">True to the mural, approximate on light — attached to your order so we match what you saw.</p>` : ""}
          <button class="wlm-btn" id="wlmAiGo" style="padding:8px 16px;font-size:11px">Generate photoreal preview</button>
          <span class="wlm-note"> Free — up to 5 photos a day · 20–40 s</span></div>`; }
    const ou = $("#wlmOwnUp"); if (ou) ou.onclick = ownUpload;
    const au = $("#wlmAiUp"); if (au) au.onclick = () => { MC._returnAi = 1; ownUpload(); };
    const ag = $("#wlmAiGo"); if (ag) ag.onclick = aiRun; }
  function ownUpload() { pickFile(async d => { MC.ownSrc = d; MC.ownQuad = null; MC.ownOff = 0; MC.ownEdit = true;
    _ownImg = await loadImg(d);
    MC.tab = "own"; tabsUI(); paneUI(); guide(true); draw(); }); }  // _returnAi survives — Done sends them back
  $("#wlmDone").onclick = () => { MC.ownEdit = false; guide(false);
    if (MC._returnAi) { MC._returnAi = 0; MC.tab = "ai"; tabsUI(); paneUI(); }
    draw(); };

  // ---------- order strip (drag to reorder) ----------
  function orderUI() {
    const el = $("#wlmOrder");
    el.style.display = MC.members.length ? "flex" : "none";
    el.innerHTML = `<span class="wlm-note" style="margin:0">On the wall — drag to reorder:</span>` +
      MC.members.map((i, k) => `<span class="oc" data-k="${k}" data-i="${i}">
        <img src="${API}/flat320/${i}.jpg" alt=""><span class="x" data-xk="${k}" title="remove">✕</span><span class="rr2" data-rk="${k}" title="swap for another artwork">↻</span></span>`).join("");
    $$("#wlmOrder .rr2").forEach(x => x.onpointerdown = e => { e.stopPropagation();
      const k = +x.dataset.rk, cur = byI(MC.members[k]);
      const pool = DESIGNS.filter(d => (!cur || d.o === cur.o) && !MC.members.includes(d.i));
      if (!pool.length) return;
      MC.members[k] = pool[Math.floor(Math.random() * pool.length)].i;
      _ckey = ""; persist(); orderUI(); cardsUI(); refresh(); });
    $$("#wlmOrder .x").forEach(x => x.onpointerdown = e => { e.stopPropagation();
      MC.members.splice(+x.dataset.xk, 1);   // remove THIS copy only — the same artwork may repeat
      _ckey = ""; persist(); orderUI(); cardsUI(); refresh(); });
    $$("#wlmOrder .oc").forEach(oc => { oc.onpointerdown = e => {
      e.preventDefault(); try{oc.setPointerCapture(e.pointerId)}catch(x){} oc.classList.add("dragging");
      oc.onpointermove = ev => {
        const over = document.elementFromPoint(ev.clientX, ev.clientY);
        const tgt = over && over.closest ? over.closest(".oc") : null;
        if (tgt && tgt !== oc) { const to = +tgt.dataset.k;
          const m = MC.members; const [it] = m.splice(+oc.dataset.k, 1); m.splice(to, 0, it);
          _ckey = ""; orderUI();
          const again = $(`#wlmOrder .oc[data-k="${to}"]`); if (again) again.classList.add("dragging"); } };
      oc.onpointerup = () => { oc.classList.remove("dragging"); oc.onpointermove = null;
        persist(); cardsUI(); refresh(); }; }; }); }

  // ---------- colour & mood + material + panels ----------
  function ctrlsUI() {
    $("#wlmMoods").innerHTML = Object.keys(MMOODS).map(mk =>
      `<button class="wlm-chip ${MC.mood === mk ? "on" : ""}" data-mood="${mk}" style="padding:5px 9px"><span style="display:inline-block;width:11px;height:11px;border-radius:50%;background:${MOOD_TINT[mk]};margin-right:5px;vertical-align:-1px"></span>${MOOD_NAME[mk]}</button>`).join("");
    $$("#wlmMoods [data-mood]").forEach(b => b.onclick = () => { MC.mood = b.dataset.mood; _ckey = ""; persist(); ctrlsUI(); refresh(); });
    const T = MC.tone;
    $("#wlmTb").value = Math.round(T.b * 100); $("#wlmTc").value = Math.round(T.c * 100); $("#wlmTs").value = Math.round(T.s * 100);
    $("#wlmTbV").textContent = Math.round(T.b * 100) + "%"; $("#wlmTcV").textContent = Math.round(T.c * 100) + "%"; $("#wlmTsV").textContent = Math.round(T.s * 100) + "%";
    const wire = (id, key) => { const el = $(id); el.oninput = () => { MC.tone[key] = (+el.value) / 100;
      $(id + "V").textContent = el.value + "%"; _ckey = "";
      clearTimeout(el._h); el._h = setTimeout(() => { persist(); refresh(); }, 350); }; };
    wire("#wlmTb", "b"); wire("#wlmTc", "c"); wire("#wlmTs", "s");
    $("#wlmTreset").onclick = () => { MC.tone = { b: 1, c: 1, s: 1 }; _ckey = ""; persist(); ctrlsUI(); refresh(); };
    $("#wlmGridT").checked = MC.grid;
    const roll = MATS[MC.material] ? MATS[MC.material][2] : 320;
    const custom = typeof MC.panel === "number" && ![60, 80, 100, 120].includes(MC.panel);
    $("#wlmPanels").innerHTML =
      [60, 80, 100, 120].map(p => `<button class="wlm-chip ${MC.panel === p ? "on" : ""}" data-p="${p}">${p} cm</button>`).join("") +
      `<span style="white-space:nowrap" title="any panel width you like"><input type="number" class="wlm-num" id="wlmPanIn" placeholder="own width" value="${custom ? MC.panel : ""}" min="30" max="${MAX_WALL}" style="width:104px;padding:5px 7px"> cm</span>`;
    $$("#wlmPanels [data-p]").forEach(b => b.onclick = () => {
      MC.panel = +b.dataset.p;
      MC.walls.forEach(wl => wl.cuts = null);
      persist(); ctrlsUI(); rulersUI(); refresh(); });
    $("#wlmPanIn").onchange = () => { const v = Math.round(+$("#wlmPanIn").value || 0);
      if (!(v >= 30)) { toast("Panels start at 30 cm wide."); return; }
      MC.panel = v; MC.walls.forEach(wl => wl.cuts = null); persist(); ctrlsUI(); rulersUI(); refresh(); };
    $("#wlmMats").innerHTML = Object.entries(MATS).map(([k, v]) => `<button class="wlm-chip wlm-mat ${MC.material === k ? "on" : ""}" data-m="${k}"><img src="${API}/rooms3d/sw_${k}.jpg" alt="" onerror="this.style.display='none'">${v[0]}</button>`).join("");
    $$("#wlmMats [data-m]").forEach(b => b.onclick = () => { MC.material = b.dataset.m;
      const r2 = MATS[MC.material][2];
      persist(); ctrlsUI(); rulersUI(); refresh(); });
    $("#wlmMatD").innerHTML = `<b>${MATS[MC.material][0]}</b> — ${MATS[MC.material][1]} <i>Texture images are illustrative — supplier photographs to follow. Full technical data on request.</i>`;
    // accordion headers always show the CURRENT choice — scannable without opening
    const setv = (id, txt) => { const e = $(id); if (e) e.textContent = txt; };
    setv("#wlmAccPanV", MC.panel === "one" ? "one seamless piece" : MC.panel + " cm panels");
    setv("#wlmAccColV", MOOD_NAME[MC.mood] + (MC.tone.b !== 1 || MC.tone.c !== 1 || MC.tone.s !== 1 ? " · adjusted" : ""));
    setv("#wlmAccMatV", MATS[MC.material][0]);
    rulersUI(); }

  // ---------- themes (dropdown) ----------
  function themesUI() {
    const mine = myThemes();
    const sel = $("#wlmTheme");
    sel.innerHTML = `<option value="">Themes — or search below</option>` +
      mine.map((t, k) => `<option value="my${k}" ${THEME === "my" + k ? "selected" : ""}>★ ${esc(t.name)} (${t.indices.length})</option>`).join("") +
      THEMES.map(t => `<option value="${t.key}" ${THEME === t.key ? "selected" : ""}>${esc(t.label)} — ${esc(t.line)}</option>`).join("");
    sel.onchange = () => { THEME = sel.value; PAGE = 1;
      if (!THEME) { SR = null; $("#wlmEcho").textContent = ""; cardsUI(); return; }
      const t = THEME.startsWith("my") ? { indices: (mine[+THEME.slice(2)] || {}).indices || [] } : THEMES.find(x => x.key === THEME);
      SR = (t && t.indices) || [];
      $("#wlmEcho").textContent = "Tap artworks to put them on the wall — or refine with the search.";
      cardsUI(); };
    $("#wlmSaveTheme").onclick = () => {
      if (!MC.members.length) { toast("Put some artworks on the wall first."); return; }
      const name = ($("#wlmQ").value || "").trim() || "My theme " + (mine.length + 1);
      const mine2 = myThemes(); mine2.unshift({ name: name.slice(0, 30), indices: MC.members.slice() });
      const replaced = mine2.length > 3;
      try { localStorage.wl_my_themes = JSON.stringify(mine2.slice(0, 3)); } catch (e) {}
      toast(`Saved as “${name.slice(0, 30)}”` + (replaced ? " — replaced your oldest theme (3 max)." : " — find it in the themes list."));
      themesUI(); };
    // phones: themes are TAPPABLE PILLS (the dropdown truncated labels) — same handler
    const tp = $("#wlmThemePills");
    if (tp) { tp.innerHTML =
        mine.map((t, k) => `<button class="wlm-chip ${THEME === "my" + k ? "on" : ""}" data-th="my${k}">★ ${esc(t.name)}</button>`).join("") +
        THEMES.map(t => `<button class="wlm-chip ${THEME === t.key ? "on" : ""}" data-th="${t.key}">${esc(t.label)}</button>`).join("");
      $$("#wlmThemePills [data-th]").forEach(b => b.onclick = () => {
        sel.value = THEME === b.dataset.th ? "" : b.dataset.th;   // tap again = clear
        sel.onchange(); themesUI(); }); } }

  // ---------- search / grid ----------
  function filtersUI() {
    const artists = [...new Set(DESIGNS.map(d => d.a).filter(Boolean))].sort();
    const styles = [...new Set(DESIGNS.flatMap(d => d.s || []))].sort();
    $("#wlmFilters").innerHTML =
      `<select id="wlmFa"><option value="">All artists (${artists.length})</option>${artists.map(a => `<option ${F.artist === a ? "selected" : ""}>${esc(a)}</option>`).join("")}</select>
       <select id="wlmFs"><option value="">All styles</option>${styles.map(s => `<option ${F.style === s ? "selected" : ""}>${esc(s)}</option>`).join("")}</select>
       <span class="wlm-note" style="margin:0">colour</span>` +
      Object.entries(DOTS).map(([n, h]) => `<button data-c="${n}" title="${n}" style="width:18px;height:18px;border-radius:50%;padding:0;cursor:pointer;border:2px solid #fff;outline:${F.colours.includes(n) ? "2px solid #1b1b1b" : "1px solid #ddd"};background:${h}"></button>`).join("");
    $("#wlmFa").onchange = e => { F.artist = e.target.value; PAGE = 1; cardsUI(); };
    $("#wlmFs").onchange = e => { F.style = e.target.value; PAGE = 1; cardsUI(); };
    $$("#wlmFilters [data-c]").forEach(b => b.onclick = () => { const n = b.dataset.c;
      const k = F.colours.indexOf(n);
      if (k >= 0) F.colours.splice(k, 1); else if (F.colours.length < 3) F.colours.push(n);
      PAGE = 1; filtersUI(); cardsUI(); });
    const nf = (F.artist ? 1 : 0) + (F.style ? 1 : 0) + F.colours.length;
    const ft = $("#wlmFiltT");
    const fw = $("#wlmFiltW");
    if (ft) ft.textContent = (fw && fw.style.display === "block" ? "Filters ▴" : "Filters ▾") + (nf ? ` · ${nf}` : ""); }
  function listNow() { let list = DESIGNS;
    if (SR) { const pos = {}; SR.forEach((i, k) => pos[i] = k);
      list = list.filter(d => pos[d.i] !== undefined).sort((a, b) => pos[a.i] - pos[b.i]); }
    return list.filter(d => (!F.artist || d.a === F.artist) && (!F.style || (d.s || []).includes(F.style)) &&
      F.colours.every(n => (d.c || []).some(x => x.toLowerCase().includes(n.toLowerCase())))); }
  function cardsUI() {
    const list = listNow();
    const mob = matchMedia("(max-width:640px)").matches;   // phones: ONE endless swipe row
    const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    if (PAGE > pages) PAGE = pages;
    const slice = mob ? list : list.slice((PAGE - 1) * PAGE_SIZE, PAGE * PAGE_SIZE);
    $("#wlmCards").innerHTML = slice.map(d => { const n = MC.members.filter(x => x === d.i).length;
      return `
      <div class="wlm-card ${n ? "on" : ""}" data-i="${d.i}" title="${n ? "tap again for another copy" : "tap to put it on the wall"}">
       <span class="ck">${n > 1 ? "×" + n : n ? "✓" : "+"}</span>
       <div class="im"><img loading="lazy" src="${API}/flat320/${d.i}.jpg" alt="${esc(d.t)}"></div>
       <div class="t">${esc(d.t)}</div><div class="a">${d.a && d.a !== "Various Artists" ? esc(d.a) : ""}</div></div>`; }).join("") ||
      `<p class="wlm-note" style="grid-column:1/-1;text-align:center;padding:22px 0">Nothing matches that combination — clear the search or the filters above.</p>`;
    $$("#wlmCards .wlm-card").forEach(c => c.onclick = () => toggleArt(c.dataset.i));
    let nums = [];
    for (let p = 1; p <= pages; p++) if (p === 1 || p === pages || Math.abs(p - PAGE) <= 2) nums.push(p);
    nums = nums.filter((p, k) => nums.indexOf(p) === k);
    let html = `<button data-pg="${PAGE - 1}" ${PAGE <= 1 ? "disabled" : ""} title="back">‹</button>`;
    let last = 0;
    for (const p of nums) { if (p - last > 1) html += `<span class="wlm-note" style="margin:0">…</span>`;
      html += `<button data-pg="${p}" class="${p === PAGE ? "on" : ""}">${p}</button>`; last = p; }
    html += `<button data-pg="${PAGE + 1}" ${PAGE >= pages ? "disabled" : ""} title="forward">›</button>
      <span class="wlm-note" style="margin:0 0 0 8px">${list.length} artworks</span>`;
    $("#wlmPager").innerHTML = mob ? "" : (pages > 1 ? html : `<span class="wlm-note" style="margin:0">${list.length} artworks</span>`);
    $$("#wlmPager [data-pg]").forEach(b => b.onclick = () => { PAGE = +b.dataset.pg;
      cardsUI(); $("#wlmCards").scrollIntoView({ behavior: "smooth", block: "start" }); }); }
  // 6 artworks max for normal walls; long runs (4-wall rooms) scale up to 12 so the
  // room can actually be filled (spec change driven by Summer's 4-wall instruction 2026-07-20)
  const memberCap = () => Math.min(12, Math.max(6, Math.ceil(totalW() / 150)));
  function toggleArt(i) {   // tap = ADD (the same artwork may repeat); remove with ✕ on the strip
    if (MC.members.length >= memberCap()) { toast(`This wall run blends up to ${memberCap()} pieces — remove one from the strip first.`); return; }
    MC.members = [...MC.members, i];
    if (MC.members.filter(x => x === i).length === 2) toast("Added a second copy — remove either with ✕ on the strip.", 3000);
    _ckey = ""; persist(); orderUI(); cardsUI(); refresh(); }
  async function search() {
    const q = ($("#wlmQ").value || "").trim(); const echo = $("#wlmEcho");
    THEME = ""; themesUI();
    if (!q) { SR = null; PAGE = 1; echo.textContent = ""; cardsUI(); return; }
    echo.textContent = "Reading the collection…";
    try { const r = await api("search", { q });
      SR = (r.results || []).map(c => c.index);
      echo.textContent = SR.length ? `Matches for “${q}” — ${SR.length} artworks` : `No match for “${q}” yet — try different words.`;
    } catch (e) { echo.textContent = "Search hiccup — please try again."; }
    PAGE = 1; cardsUI(); }
  function camSearch() { pickFile(async d => { $("#wlmEcho").textContent = "Reading your picture…";
    THEME = ""; themesUI();
    const r = await api("image-search", { image: d });
    if (!r.ok) { $("#wlmEcho").textContent = r.message || "Could not read that picture."; return; }
    SR = (r.results || []).map(c => c.index).filter(Boolean);
    $("#wlmEcho").textContent = r.message || (SR.length ? "Closest artworks we carry." : "No close match.");
    PAGE = 1; cardsUI(); }); }

  // ---------- price ----------
  function wallsPayload() { return MC.walls.map(wl => ({ w: wl.w, cuts: MC.panel === "one" ? [] : wallCuts(wl), cutouts: wl.cutouts || [] })); }
  async function refresh() {
    wallsUI(); rulersUI();
    const mem = MC.members;
    const priceEl = $("#wlmPrice"), dealEl = $("#wlmDeal"), atc = $("#wlmAtc");
    if (!mem.length) { priceEl.textContent = "—"; dealEl.textContent = "Add an artwork to see the price for your walls.";
      atc.disabled = true; QUOTE = null; draw(); return; }
    draw();   // the room answers instantly — the server quote follows, debounced
    $("#wlmPanelN").textContent = MC.panel === "one"
      ? `${MC.walls.length} seamless piece${MC.walls.length > 1 ? "s" : ""} — one per wall, no joins. Cutting is free either way.`
      : `${panelCount()} panels — machine-cut to the millimetre, hung edge to edge. Cutting is free.`;
    priceEl.style.opacity = .55;
    clearTimeout(refresh._h);
    refresh._h = setTimeout(async () => {
      const r = await api("mural/preview", { indices: mem, moods: mem.map(() => MC.mood),
        wall_w: totalW(), wall_h: MC.h, material: MC.material,
        panel_width: typeof MC.panel === "number" ? MC.panel : 0,
        offset: MC.offset, walls: wallsPayload(), tone: MC.tone });
      priceEl.style.opacity = 1;
      if (!r.ok) { priceEl.textContent = "—"; dealEl.textContent = r.message || ""; atc.disabled = true; QUOTE = null; return; }
      QUOTE = r;
      priceEl.innerHTML = r.discounted < r.price ? `<span class="wlm-strike">${money(r.price)}</span>${money(r.discounted)}` : money(r.discounted);
      // On artist linen every rate doubles — say so, or the line contradicts the
      // page lede's 150→100 story (Summer's tick 2026-07-30).
      const linen2 = MC.material === "artist_linen" || MC.material === "linen_textile";
      dealEl.innerHTML = `${r.m2} m² of wall. ` + (r.discount_rate ? `<b>${Math.round(r.discount_rate * 100)}% off the top rate.</b> ` : "") +
        (linen2 ? "On artist linen (twice the standard rate) " : "") +
        (r.next_band ? `${linen2 ? "the" : "Past " + r.next_band.at + " m² the"} rate falls to ${money(r.next_band.rate_m2)} per m²${linen2 ? " past " + r.next_band.at + " m²" : ""}. ` : "") +
        `${money(r.rate_m2)} per m² effective.`;
      atc.disabled = !!MC._short;
    }, 420); }

  // ---------- drawing: real room photo ----------
  function contW() { const w = Math.min(1240, ($("#wlm").clientWidth || 0) - 2);
    return w >= 320 ? w : 1200; }
  async function draw() {
    const cv = $("#wlmCv"), conf = $("#wlmConfirm");
    const comp = await compose();
    const tw = totalW();
    orderUI();
    const atc = $("#wlmAtc");
    if (!comp) { if (T3 && T3.cv) T3.cv.style.display = "none"; cv.style.display = "block";
      disp("#wlmZoomers", "none"); disp("#wlmJoy", "none");
      if (atc) atc.textContent = "Pick artwork to start";
      const W = contW(); cv.width = W; cv.height = Math.round(W * .3);
      const x = cv.getContext("2d"); x.fillStyle = "#f4f4f3"; x.fillRect(0, 0, cv.width, cv.height);
      x.fillStyle = "#8a8578"; x.font = "22px " + (getComputedStyle(document.body).fontFamily || "serif");
      x.textAlign = "center"; x.fillText("Pick a theme or tap artworks to begin your mural.", cv.width / 2, cv.height / 2);
      // #wlmSel was the openings toolbar, retired 2026-07-20 — the leftover hide
      // threw "null .style" here whenever the empty state drew (eyes WARN 2026-08-12)
      conf.textContent = ""; if (atc) atc.disabled = true; GEOP = null; return; }
    const win = windowOfCached(comp);
    MC._short = win.short;
    if (win.short) {
      conf.innerHTML = `<span class="wlm-warn"><b>Covers ${(win.coverCm / 100).toFixed(1)} of ${(tw / 100).toFixed(1)} m</b> — add more art to fill the wall.</span>`;
      if (atc) { atc.disabled = true;
        atc.textContent = `Add more art — covers ${(win.coverCm / 100).toFixed(1)} of ${(tw / 100).toFixed(1)} m`; }
    } else {
      conf.innerHTML = `✓ <b>This exact view is what we print</b> — ${MC.walls.map(w => w.w).join(" + ")} × ${MC.h} cm, ${MC.panel === "one" ? MC.walls.length + " seamless piece" + (MC.walls.length > 1 ? "s" : "") : panelCount() + " panels"}, ${MATS[MC.material][0]}.`;
      if (atc) { atc.textContent = "Add mural to cart"; if (QUOTE) atc.disabled = false; } }
    if (MC.tab === "tpl") {
      if (window.THREE && !t3Init().fail) {
        cv.style.display = "none"; T3.cv.style.display = "block"; t3Draw(win); selToolUI();
        disp("#wlmJoy", T3._walk ? "block" : "none");
        disp("#wlmZoomers", T3._walk ? "none" : "flex"); }
      else {
        // 3D engine absent (still downloading, blocked, or no WebGL): paint the 2D room
        // NOW — the stage must never sit empty — and upgrade to 3D silently if the lib
        // lands. Pixel-7 field failure 2026-07-20: draw() used to AWAIT the 600KB lib,
        // so a stalled fetch left "price but no demo" with no error.
        if (T3 && T3.cv) T3.cv.style.display = "none";
        cv.style.display = "block"; disp("#wlmJoy", "none"); disp("#wlmZoomers", "none");
        drawRoom(cv, win);
        if (!window.THREE) libLoad().then(ok => { if (ok && MC.tab === "tpl") draw(); });
      }
    }
    else { if (T3 && T3.cv) T3.cv.style.display = "none"; cv.style.display = "block";
      disp("#wlmJoy", "none"); disp("#wlmZoomers", "none");
      if (MC.ownSrc) drawOwn(cv, win);
      else { const W = contW(); cv.width = W; cv.height = Math.round(W * .28);
        const x = cv.getContext("2d"); x.fillStyle = "#f4f4f3"; x.fillRect(0, 0, W, cv.height); GEOP = null; } } }
  // the canvas the customer is LOOKING at (3D room on the demo tab, 2D elsewhere) —
  // save / email / AI photo must snapshot this one
  const viewCanvas = () => (MC.tab === "tpl" && T3 && !T3.fail && T3.cv.style.display !== "none") ? T3.cv : $("#wlmCv");

  // ===== PARAMETRIC ROOM: geometry from the customer's real wall sizes =====
  let FLOORIMG = null, GEOP = null;
  function roomLayout() {
    const w = MC.walls.map(x => x.w), n = w.length;
    if (n === 1) return { segs: [[{ x: -w[0] / 2, z: 0 }, { x: w[0] / 2, z: 0 }]], depth: Math.max(300, w[0] * .5) };
    if (n === 2) { const c = Math.SQRT1_2;
      return { segs: [[{ x: -w[0] * c, z: w[0] * c }, { x: 0, z: 0 }], [{ x: 0, z: 0 }, { x: w[1] * c, z: w[1] * c }]],
               depth: Math.max(w[0], w[1]) * c + 160 }; }
    const b = w[1], l = w[0], r = w[2];
    if (n === 3)
      return { segs: [[{ x: -b / 2, z: l }, { x: -b / 2, z: 0 }], [{ x: -b / 2, z: 0 }, { x: b / 2, z: 0 }], [{ x: b / 2, z: 0 }, { x: b / 2, z: r }]],
               depth: Math.max(l, r) + 140 };
    // n === 4: the mural wraps the WHOLE room — left, back, right, then the front wall
    // running back toward the start. Each wall keeps its own true length (90° corners);
    // if the four sizes don't form a perfect rectangle, plaster fills the honest gap.
    const f4 = w[3];
    return { segs: [[{ x: -b / 2, z: l }, { x: -b / 2, z: 0 }], [{ x: -b / 2, z: 0 }, { x: b / 2, z: 0 }],
                    [{ x: b / 2, z: 0 }, { x: b / 2, z: r }], [{ x: b / 2, z: r }, { x: b / 2 - f4, z: r }]],
             closed: true, depth: Math.max(l, r) + 140 }; }
  async function drawRoom(cv, win) {
    if (!FLOORIMG) FLOORIMG = await loadImg(API + FLOOR_TX);
    const W = contW(), lay = roomLayout(), Hcm = MC.h;
    const camY = 150;                                   // eye height, cm
    const camZ = lay.depth * 1.15 + 160;
    const yaw = Math.max(-.6, Math.min(.6, MC.yaw || 0)), cyw = Math.cos(yaw), syw = Math.sin(yaw);
    let f = W * .9;
    const proj = (x, y, z) => { const dz0 = camZ - z;
      const Xc = x * cyw - dz0 * syw, Zc = Math.max(40, dz0 * cyw + x * syw);
      return [f * Xc / Zc, f * (camY - y) / Zc]; };
    let top = 1e9, bot = -1e9, lft = 1e9, rgt = -1e9;
    const extents = () => { top = 1e9; bot = -1e9; lft = 1e9; rgt = -1e9;
      for (const seg of lay.segs) for (const p of seg) for (const y of [0, Hcm]) {
        const a = proj(p.x, y, p.z);
        top = Math.min(top, a[1]); bot = Math.max(bot, a[1]);
        lft = Math.min(lft, a[0]); rgt = Math.max(rgt, a[0]); } };
    extents();
    const fitH = W * .48, fitW = W - 20;
    const shrink = Math.min(1, fitH / (bot - top), fitW / (rgt - lft));
    if (shrink < 1) { f *= shrink; extents(); }
    const xOff = W / 2 - (lft + rgt) / 2;
    const floorPad = Math.min(150, (bot - top) * .42), pad = 20;
    const yOff = pad - top;
    const H = Math.round(bot - top + pad * 2 + floorPad);
    cv.width = W; cv.height = H;
    const x = cv.getContext("2d"); x.imageSmoothingQuality = "high";
    const P = (px, py, pz) => { const [a, b] = proj(px, py, pz); return [a + xOff, b + yOff]; };
    // walls above/behind + ceiling wash
    x.fillStyle = "#eceae3"; x.fillRect(0, 0, W, H);
    const cg = x.createLinearGradient(0, 0, 0, Math.max(30, top + yOff));
    cg.addColorStop(0, "rgba(255,255,255,.65)"); cg.addColorStop(1, "rgba(255,255,255,0)");
    x.fillStyle = cg; x.fillRect(0, 0, W, Math.max(30, top + yOff));
    // FLOOR: real oak texture, perspective-projected
    const xsAll = lay.segs.flatMap(s => s.map(p => p.x));
    const fx0 = Math.min(...xsAll) - 500, fx1 = Math.max(...xsAll) + 500;
    const fq = [P(fx0, 0, Math.min(0, ...lay.segs.flatMap(s => s.map(p => p.z)))),
                P(fx1, 0, 0), P(fx1, 0, camZ - 60), P(fx0, 0, camZ - 60)];
    if (FLOORIMG) {
      // tile at REAL scale: the sampled patch covers ~300 cm of floor width
      const spanX = fx1 - fx0, spanZ = camZ - 60 - Math.min(0, ...lay.segs.flatMap(s => s.map(p => p.z)));
      const cell = 300, nx = Math.max(1, Math.min(10, Math.round(spanX / cell))), ny = Math.max(1, Math.min(8, Math.round(spanZ / cell)));
      const cw = 300, ch = 480;   // planks run INTO the room (rotated 90°)
      const tile = document.createElement("canvas"); tile.width = nx * cw; tile.height = ny * ch;
      const tx2 = tile.getContext("2d");
      for (let iy = 0; iy < ny; iy++) for (let ix = 0; ix < nx; ix++) {
        tx2.save(); tx2.translate(ix * cw + cw / 2, iy * ch + ch / 2);
        tx2.rotate(Math.PI / 2);
        tx2.scale(ix % 2 ? -1 : 1, iy % 2 ? -1 : 1);
        tx2.drawImage(FLOORIMG, -ch / 2, -cw / 2, ch, cw); tx2.restore(); }
      drawWarped(x, tile, fq, 12);
      x.save(); x.globalAlpha = .12; x.fillStyle = "#f2ede4";   // soften the grain
      x.beginPath(); x.moveTo(fq[0][0], fq[0][1]); fq.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.fill(); x.restore();
    } else { x.fillStyle = "#d8d2c6";
      x.beginPath(); x.moveTo(fq[0][0], fq[0][1]); fq.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.fill(); }
    // WALLS (each = the mural slice at TRUE size — the wall extends, the art never squeezes)
    GEOP = { quads: [], visCm: [] };
    const order = lay.segs.length === 3 ? [0, 2, 1] : lay.segs.map((_, i) => i);
    const fam = getComputedStyle(document.body).fontFamily || "sans-serif";
    for (const k of order) {
      const seg = lay.segs[k], wl = MC.walls[k];
      const q = [P(seg[0].x, Hcm, seg[0].z), P(seg[1].x, Hcm, seg[1].z), P(seg[1].x, 0, seg[1].z), P(seg[0].x, 0, seg[0].z)];
      if (win) { const { t } = wallTexture(win, k); drawWarped(x, t, q, 14); GEOP.visCm[k] = wl.w; }
      else { x.fillStyle = "#e9e6df";
        x.beginPath(); x.moveTo(q[0][0], q[0][1]); q.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.fill();
        if (win && win.short) { x.strokeStyle = "rgba(163,51,51,.5)"; x.setLineDash([7, 6]);
          x.beginPath(); x.moveTo(q[0][0], q[0][1]); q.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.stroke(); x.setLineDash([]); }
        GEOP.visCm[k] = 0; }
      // light: ceiling-to-floor wash + side-wall depth tint
      const gq = x.createLinearGradient(0, Math.min(q[0][1], q[1][1]), 0, Math.max(q[2][1], q[3][1]));
      gq.addColorStop(0, "rgba(0,0,0,.10)"); gq.addColorStop(.25, "rgba(0,0,0,0)"); gq.addColorStop(1, "rgba(0,0,0,.05)");
      x.save(); x.beginPath(); x.moveTo(q[0][0], q[0][1]); q.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath();
      x.clip(); x.fillStyle = gq; x.fillRect(0, 0, W, H);
      if (lay.segs.length > 1 && k !== (lay.segs.length === 3 ? 1 : -1))
        { x.fillStyle = k === 0 ? "rgba(0,0,0,.07)" : "rgba(255,255,255,.05)"; x.fillRect(0, 0, W, H); }
      x.restore();
      // cornice + floor contact shadow
      x.strokeStyle = "rgba(255,255,255,.55)"; x.lineWidth = 2;
      x.beginPath(); x.moveTo(q[0][0], q[0][1]); x.lineTo(q[1][0], q[1][1]); x.stroke();
      const shq = [q[3], q[2], [q[2][0], q[2][1] + 7], [q[3][0], q[3][1] + 7]];
      const sg = x.createLinearGradient(0, Math.min(q[2][1], q[3][1]), 0, Math.max(q[2][1], q[3][1]) + 8);
      sg.addColorStop(0, "rgba(0,0,0,.18)"); sg.addColorStop(1, "rgba(0,0,0,0)");
      x.fillStyle = sg;
      x.beginPath(); x.moveTo(shq[0][0], shq[0][1]); shq.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.fill();
      if (MC.sel === k && MC._flashUntil && Date.now() < MC._flashUntil) {
        x.strokeStyle = "rgba(74,82,48,.8)"; x.lineWidth = 3; x.setLineDash([10, 7]);
        x.beginPath(); x.moveTo(q[0][0], q[0][1]); q.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.stroke();
        x.setLineDash([]); x.lineWidth = 1;
        clearTimeout(MC._flashT); MC._flashT = setTimeout(draw, 750); }
      GEOP.quads[k] = q;
      const lx2 = (q[2][0] + q[3][0]) / 2, ly2 = Math.min(H - 8, Math.max(q[2][1], q[3][1]) + 16);
      x.font = "11px " + fam; x.fillStyle = "#6f6a5f"; x.textAlign = "center";
      x.fillText(`Wall ${k + 1} — ${wl.w} cm${MC.sel === k ? " ✓" : ""}`, lx2, ly2); }
    selToolUI();
    wireRoom(cv); }
  // (2D fallback keeps always-visible chips — hover states are a 3D-path refinement)
  function selToolUI() {}   // openings toolbar retired 2026-07-20 — canvas-only editing
  function wireRoom(cv) {   // re-wired every draw — tabs share the canvas
    const Ppt = ev => { const r = cv.getBoundingClientRect(); const t = ev.touches ? ev.touches[0] : ev;
      return [(t.clientX - r.left) * cv.width / r.width, (t.clientY - r.top) * cv.height / r.height]; };
    const inQuad = (p, q) => { let inside = false;
      for (let i = 0, j = 3; i < 4; j = i++) { const [xi, yi] = q[i], [xj, yj] = q[j];
        if ((yi > p[1]) !== (yj > p[1]) && p[0] < (xj - xi) * (p[1] - yi) / (yj - yi) + xi) inside = !inside; }
      return inside; };
    const toPlane = (p, k) => { const q = GEOP.quads[k];
      const inv = solveH(q, [[0, 0], [1, 0], [1, 1], [0, 1]]);
      const [u, v] = applyH(inv, p[0], p[1]);
      return { u, v, cmX: u * MC.walls[k].w, cmY: MC.h - v * MC.h }; };
    const hit = p => {
      if (!GEOP || MC.tab !== "tpl") return null;
      for (let k = GEOP.quads.length - 1; k >= 0; k--) {
        if (!GEOP.quads[k] || !inQuad(p, GEOP.quads[k])) continue;
        const pl = toPlane(p, k), wl = MC.walls[k];
        for (let ci = (wl.cutouts || []).length - 1; ci >= 0; ci--) { const co = wl.cutouts[ci];
          const inX = pl.cmX >= co.x && pl.cmX <= co.x + co.w, inY = pl.cmY >= co.y && pl.cmY <= co.y + co.h;
          const nearTR = Math.abs(pl.cmX - (co.x + co.w)) < 16 && Math.abs(pl.cmY - (co.y + co.h)) < 16;
          if (nearTR) return { kind: "codel", k, ci };   // ✕ chip — remove the opening
          const nearBR = Math.abs(pl.cmX - (co.x + co.w)) < 14 && Math.abs(pl.cmY - co.y) < 14;
          if (nearBR) return { kind: "cosize", k, ci };
          if (inX && inY) return { kind: "comove", k, ci, dx: pl.cmX - co.x, dy: pl.cmY - co.y }; }
        return { kind: "pan", k, startOffset: MC.offset, startX: p[0], moved: false }; }
      return { kind: "yaw", x0: p[0], yaw0: MC.yaw || 0, moved: false };   // floor / background = look around
    };
    cv.onmousedown = cv.ontouchstart = ev => {
      if (MC.tab !== "tpl") return;
      const p = Ppt(ev); const d = hit(p);
      if (!d) return;
      cv._dP = d; ev.preventDefault(); };
    cv.onmousemove = cv.ontouchmove = ev => {
      if (MC.tab !== "tpl") return;
      const p = Ppt(ev);
      if (cv._dP) { const d = cv._dP;
        if (d.kind === "yaw") { if (Math.abs(p[0] - d.x0) > 4) d.moved = true;
          MC.yaw = Math.max(-.6, Math.min(.6, d.yaw0 - (p[0] - d.x0) / cv.width * 1.4));
          clearTimeout(cv._hY); cv._hY = setTimeout(draw, 30); }
        else if (d.kind === "pan") { if (Math.abs(p[0] - d.startX) > 6) d.moved = true;
          if (d.moved && !MC._short) { compose().then(comp => { if (!comp) return;
            const winW = Math.round(comp.H * totalW() / MC.h);
            const q = GEOP.quads[d.k];
            const wallPx = (Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]) + Math.hypot(q[2][0] - q[3][0], q[2][1] - q[3][1])) / 2;
            const pxPerCompPx = wallPx / (MC.walls[d.k].w / totalW() * winW);
            const range = comp.W - winW; if (range <= 0) return;
            MC.offset = Math.max(0, Math.min(1, d.startOffset - (p[0] - d.startX) / (range * pxPerCompPx)));
            draw(); clearTimeout(cv._hP); cv._hP = setTimeout(() => { persist(); refresh(); }, 500); }); } }
        else { const pl = toPlane(p, d.k), wl = MC.walls[d.k], co = wl.cutouts[d.ci];
          if (!co) return;
          if (d.kind === "comove") {
            co.x = Math.max(0, Math.min(wl.w - co.w, Math.round(pl.cmX - d.dx)));
            co.y = Math.max(0, Math.min(MC.h - co.h, Math.round(pl.cmY - d.dy))); }
          if (d.kind === "cosize") {
            co.w = Math.max(30, Math.min(wl.w - co.x, Math.round(pl.cmX - co.x)));
            const topCm = co.y + co.h;
            let nb = Math.max(0, Math.min(topCm - 30, Math.round(pl.cmY)));
            co.y = nb; co.h = topCm - nb; }
          clearTimeout(cv._hP2); cv._hP2 = setTimeout(() => { draw(); selToolUI(); }, 60); }
        ev.preventDefault(); return; }
      const d = hit(p);
      cv.style.cursor = d ? (d.kind === "pan" ? "grab" : d.kind === "yaw" ? "ew-resize" : d.kind === "cosize" ? "nwse-resize" : d.kind === "codel" ? "pointer" : "move") : "default"; };
    const up = () => { if (cv._dP) { const d = cv._dP;
      if (d.kind === "codel") { (MC.walls[d.k].cutouts || []).splice(d.ci, 1);
        persist(); draw(); toast("Opening removed."); }
      else if (d.kind === "pan" && !d.moved) { MC.sel = d.k; MC._flashUntil = Date.now() + 700; draw(); }
      else if (d.kind !== "yaw") { persist(); refresh(); } }
      cv._dP = null; };
    if (cv._upPh) window.removeEventListener("mouseup", cv._upPh);
    cv._upPh = up; window.addEventListener("mouseup", up); cv.ontouchend = up; }

  // ===== v7 GAME ROOM (three.js, self-hosted lib) — a real interior built from the
  // customer's wall sizes: enclosed room (floor/ceiling/side walls), PBR oak floor,
  // environment lighting + ACES, 3D doors/windows with ✕/resize chips. The mural maths
  // (compose/windowOf/wallTexture) is UNTOUCHED — this is presentation only. =====
  let T3 = null;
  function libLoad() {
    if (window.THREE) return Promise.resolve(true);
    if (new URLSearchParams(location.search).get("wlm2d")) return Promise.resolve(false);   // debug: force 2D
    if (!libLoad._p) libLoad._p = new Promise(res => {
      let settled = false;
      const done = ok => { if (settled) return; settled = true;
        if (!ok) { libLoad._p = null; DIAG("lib-three failed/timeout"); }   // next draw may retry
        res(ok); };
      const s = document.createElement("script");
      s.src = API + "/app/lib-three-147.js";
      s.onload = () => done(true); s.onerror = () => done(false);
      document.head.appendChild(s);
      setTimeout(() => done(!!window.THREE), 12000); });   // slow mobile data must never hang the stage
    return libLoad._p; }
  function chipTexture(kind) { const c = document.createElement("canvas"); c.width = c.height = 64;
    const x = c.getContext("2d");
    if (kind === "del") { x.fillStyle = "rgba(27,27,27,.92)"; x.beginPath(); x.arc(32, 32, 30, 0, 7); x.fill();
      x.strokeStyle = "#fff"; x.lineWidth = 7; x.lineCap = "round";
      x.beginPath(); x.moveTo(21, 21); x.lineTo(43, 43); x.moveTo(43, 21); x.lineTo(21, 43); x.stroke(); }
    else { x.fillStyle = GREEN; x.beginPath(); x.arc(32, 32, 30, 0, 7); x.fill();
      x.strokeStyle = "#fff"; x.lineWidth = 6; x.lineCap = "round";
      x.beginPath(); x.moveTo(20, 44); x.lineTo(44, 20); x.moveTo(30, 44); x.lineTo(44, 30); x.stroke(); }
    const t = new THREE.CanvasTexture(c); return t; }
  function skyTexture() { const c = document.createElement("canvas"); c.width = 4; c.height = 128;
    const x = c.getContext("2d"); const g = x.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, "#bcd2de"); g.addColorStop(.55, "#dde7e6"); g.addColorStop(1, "#f0f2e9");
    x.fillStyle = g; x.fillRect(0, 0, 4, 128); return new THREE.CanvasTexture(c); }
  function shadowTexture() { const c = document.createElement("canvas"); c.width = 4; c.height = 64;
    const x = c.getContext("2d"); const g = x.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, "rgba(0,0,0,.30)"); g.addColorStop(1, "rgba(0,0,0,0)");
    x.fillStyle = g; x.fillRect(0, 0, 4, 64); return new THREE.CanvasTexture(c); }
  function t3Init() {
    if (T3) return T3;
    try {
      const cv = document.createElement("canvas");
      cv.style.cssText = "display:none;width:100%;height:auto;touch-action:none";
      $("#wlmCv").parentElement.appendChild(cv);
      const r = new THREE.WebGLRenderer({ canvas: cv, antialias: true, preserveDrawingBuffer: true });
      r.outputEncoding = THREE.sRGBEncoding;
      r.toneMapping = THREE.ACESFilmicToneMapping; r.toneMappingExposure = 1.06;
      r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      const scene = new THREE.Scene(); scene.background = new THREE.Color(0xf2f2f1);
      const pm = new THREE.PMREMGenerator(r);
      scene.environment = pm.fromScene(new THREE.RoomEnvironment(), .04).texture;
      const sun = new THREE.DirectionalLight(0xfff6e8, .5); sun.position.set(-600, 900, 700); scene.add(sun);
      scene.add(new THREE.AmbientLight(0xffffff, .2));
      const cam = new THREE.PerspectiveCamera(46, 2, 5, 30000);
      const tl = new THREE.TextureLoader(); tl.setCrossOrigin("anonymous");
      const tex = k => tl.load(API + "/rooms3d/" + k, () => t3Render());
      const woodC = tex("wood_c.jpg"), woodN = tex("wood_n.jpg");
      for (const t of [woodC, woodN]) { t.wrapS = t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = r.capabilities.getMaxAnisotropy(); }
      woodC.encoding = THREE.sRGBEncoding;
      const shared = m => { m._shared = true; return m; };
      T3 = { cv, r, scene, cam, woodC, woodN, room: null, wallMeshes: [], texes: [],
             ray: new THREE.Raycaster(),
             floorM: shared(new THREE.MeshStandardMaterial({ map: woodC, normalMap: woodN,
               roughness: .62, metalness: 0, envMapIntensity: .55 })),
             chipDelM: shared(new THREE.MeshBasicMaterial({ map: chipTexture("del"), transparent: true, depthTest: false })),
             chipGripM: shared(new THREE.MeshBasicMaterial({ map: chipTexture("grip"), transparent: true, depthTest: false })),
             plasterT: plasterTexture(),
             skyM: shared(new THREE.MeshBasicMaterial({ map: skyTexture() })),
             shadowM: shared(new THREE.MeshBasicMaterial({ map: shadowTexture(), transparent: true,
               opacity: .48, depthWrite: false })) };
      t3Wire();
      // VR is a QUIET enhancement: the headset button only ever appears on devices
      // that really have one (Quest browser etc.) — zero clutter for everyone else
      try { if (navigator.xr && navigator.xr.isSessionSupported)
        navigator.xr.isSessionSupported("immersive-vr").then(ok => { if (ok) { T3.xrOk = true; tabsUI(); } }).catch(() => {}); } catch (e) {}
    } catch (e) { console.warn("wl 3d init", e); DIAG("t3Init fail: " + (e && e.message));
      T3 = { fail: true, cv: { style: {} } }; }
    return T3; }
  async function vrEnter() {   // WebXR: same scene, true scale, no external service, no cost
    const t = T3; if (!t || t.fail || !t.room) { toast("Put an artwork on the wall first."); return; }
    try {
      const s = await navigator.xr.requestSession("immersive-vr", { optionalFeatures: ["local-floor"] });
      t.r.xr.enabled = true;
      t.r.xr.setReferenceSpaceType("local-floor");
      await t.r.xr.setSession(s);
      // scene is authored in CENTIMETRES; XR runs in metres — scale the world and put
      // the visitor mid-room; the headset supplies eye height and every head turn
      const f = t._fit, fx2 = (f.minX + f.maxX) / 2;
      t.scene.scale.set(.01, .01, .01);
      t.scene.position.set(-fx2 * .01, 0, -(f.backZ * .55) * .01);
      const cam = t.cam; cam.position.set(0, 0, 0); cam.rotation.set(0, 0, 0);
      t.r.setAnimationLoop(() => t.r.render(t.scene, cam));
      s.addEventListener("end", () => {
        t.r.setAnimationLoop(null); t.r.xr.enabled = false;
        t.scene.scale.set(1, 1, 1); t.scene.position.set(0, 0, 0);
        t3Camera(); t3Render(); });
    } catch (e) { console.warn("wl vr", e); toast("Could not start the headset view — try again from your headset's browser."); } }
  const t3Render = () => { if (T3 && !T3.fail && T3.room) T3.r.render(T3.scene, T3.cam); };
  function plasterTexture() {   // faint painted-plaster noise so bare walls never read as a void
    const c = document.createElement("canvas"); c.width = c.height = 128;
    const x = c.getContext("2d"); x.fillStyle = "#fff"; x.fillRect(0, 0, 128, 128);
    const d = x.getImageData(0, 0, 128, 128), p = d.data;
    let s = 49;   // deterministic tiny LCG — no Math.random needed
    for (let q = 0; q < p.length; q += 4) { s = (s * 1103515245 + 12345) & 0x7fffffff;
      const n = 249 + (s % 7); p[q] = p[q + 1] = p[q + 2] = n; }
    x.putImageData(d, 0, 0);
    const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t; }
  const PLASTER = (w2, h2) => {
    const m = new THREE.MeshStandardMaterial({ color: 0xefece4, roughness: .93, metalness: 0, envMapIntensity: .6 });
    if (w2 && T3 && T3.plasterT) { const tt = T3.plasterT.clone(); tt.needsUpdate = true;
      tt.repeat.set(Math.max(1, w2 / 170), Math.max(1, h2 / 170)); m.map = tt; }
    return m; };
  const WHITE = () => new THREE.MeshStandardMaterial({ color: 0xf6f4ef, roughness: .55, metalness: 0, envMapIntensity: .7 });
  function t3Opening(wallMesh, wl, co, k, ci) {
    const g = new THREE.Group();
    g.position.set(co.x + co.w / 2 - wl.w / 2, co.y + co.h / 2 - MC.h / 2, 0);
    const fr = 7, fd = 4;   // architrave width / depth, cm
    const mkBox = (w, h, d, mat, x2, y2, z2) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x2, y2, z2); g.add(m); return m; };
    mkBox(co.w + fr * 2, fr, fd, WHITE(), 0, co.h / 2 + fr / 2, fd / 2);      // head
    mkBox(co.w + fr * 2, fr, fd, WHITE(), 0, -co.h / 2 - fr / 2, fd / 2);     // sill/foot band
    mkBox(fr, co.h, fd, WHITE(), -co.w / 2 - fr / 2, 0, fd / 2);              // jambs
    mkBox(fr, co.h, fd, WHITE(), co.w / 2 + fr / 2, 0, fd / 2);
    if (co.kind === "window") {
      const sky = new THREE.Mesh(new THREE.PlaneGeometry(co.w + 30, co.h + 30), T3.skyM);
      sky.position.z = -16; g.add(sky);
      const glass = new THREE.Mesh(new THREE.PlaneGeometry(co.w, co.h),
        new THREE.MeshPhysicalMaterial({ color: 0xdfeaec, transparent: true, opacity: .38,
          roughness: .06, metalness: 0, envMapIntensity: 1.3 }));
      glass.position.z = -2; g.add(glass);
      mkBox(4, co.h, 3, WHITE(), 0, 0, 0);                                     // mullions
      mkBox(co.w, 4, 3, WHITE(), 0, 0, 0);
      mkBox(co.w + fr * 2 + 6, 4.5, fd + 5, WHITE(), 0, -co.h / 2 - fr + 1, fd / 2 + 1); // sill ledge
    } else {
      const slab = mkBox(co.w - 2, co.h - 1, 3.4, WHITE(), 0, 0, .6);          // door slab
      slab.material.color.set(0xf8f6f1);
      const p1 = mkBox(co.w - 26, co.h * .36, 1.6, PLASTER(), 0, co.h * .215, 2.8);   // recessed panels
      const p2 = mkBox(co.w - 26, co.h * .30, 1.6, PLASTER(), 0, -co.h * .215, 2.8);
      p1.material.color.set(0xf1efe9); p2.material.color.set(0xf1efe9);
      const brass = new THREE.MeshStandardMaterial({ color: 0xa5905e, roughness: .35, metalness: .85 });
      const hy = Math.max(-co.h / 2 + 8, Math.min(co.h / 2 - 8, 100 - (co.y + co.h / 2)));
      const hm = new THREE.Mesh(new THREE.SphereGeometry(3.1, 16, 12), brass);
      hm.position.set(co.w / 2 - 9, hy, 4.6); hm.scale.z = .55; g.add(hm);
    }
    g.userData.oi = ci; g._chips = [];
    const chip = (mat, op, cx2, cy2) => { const m = new THREE.Mesh(new THREE.CircleGeometry(11, 24), mat);
      m.renderOrder = 5; m.position.set(cx2, cy2, fd + 2);
      m.visible = false;   // controls appear on hover / after a tap — never at rest
      m.userData = { kind: "chip", op, k, ci }; g.add(m); g._chips.push(m); };
    chip(T3.chipDelM, "codel", co.w / 2 + fr, co.h / 2 + fr);
    chip(T3.chipGripM, "cosize", co.w / 2 + fr, -co.h / 2 - fr);
    wallMesh.add(g); }
  function opShow(target) {   // target {k, ci} or null — reveal that opening's controls
    const t = T3; if (!t || !t.room) return;
    const key = target ? target.k + ":" + target.ci : "";
    if (t._opKey === key) return;
    t._opKey = key;
    t.wallMeshes.forEach((m, k) => m && m.children.forEach(g => { if (!g._chips) return;
      const on = !!target && target.k === k && g.userData.oi === target.ci;
      g._chips.forEach(c => c.visible = on); }));
    t3Render(); }
  function t3Camera() {
    const t = T3, f = t._fit; if (!f) return;
    const W = contW(), H = Math.round(Math.max(320, Math.min(W * .56, 700)));
    t.r.setSize(W, H, false); t.cv.style.height = "auto";
    t.cam.aspect = W / H; t.cam.fov = f.fov || 46; t.cam.updateProjectionMatrix();
    if (t._walk) { const w = t._walk;   // first-person: stand IN the room
      w.x = Math.max(f.minX + 35, Math.min(f.maxX - 35, w.x));
      w.z = Math.max(60, Math.min(f.backZ - 45, w.z));
      t.cam.position.set(w.x, 150, w.z);
      t.cam.rotation.order = "YXZ";
      t.cam.rotation.set(w.pitch, -w.yaw, 0);
      return; }
    t.cam.rotation.order = "XYZ"; t.cam.rotation.set(0, 0, 0);
    const cz = Math.max(150, Math.min(f.zoomMax || 4000, f.camZ * (MC.zoom || 1)));
    t.cam.position.set(0, 150, cz);
    t.cam.lookAt(0, Math.min(140, MC.h * .46), 0);
    t.cam.rotateY(-(MC.yaw || 0)); }
  function t3Build(win) {
    const t = T3, lay = roomLayout(), Hcm = MC.h;
    if (t.room) { t.scene.remove(t.room);
      t.room.traverse(o => { if (o.geometry) o.geometry.dispose();
        if (o.material && !o.material._shared) o.material.dispose(); });
      t.texes.forEach(x => x.dispose()); }
    t.texes = []; t.wallMeshes = [];
    const g = new THREE.Group(); t.room = g; t.scene.add(g);
    const xsAll = lay.segs.flatMap(s => s.map(p => p.x)), zsAll = lay.segs.flatMap(s => s.map(p => p.z));
    const minX = Math.min(...xsAll), maxX = Math.max(...xsAll), maxZ = Math.max(...zsAll);
    // camera distance: fit the mural walls, standing inside the room at eye height
    const W = contW(), H = Math.round(Math.max(320, Math.min(W * .56, 700)));
    const aspect = W / H;
    const vf = 46 * Math.PI / 360, hf = Math.atan(Math.tan(vf) * aspect);
    const spanX = (maxX - minX) / 2 + 85;   // breathing room — the art sells, not the crop
    const dist = Math.max(spanX / Math.tan(hf), (Hcm * .62 + 70) / Math.tan(vf));
    let camZ, roomD, frontZ = 0;
    if (lay.closed) {   // 4 mural walls enclose the room — stand INSIDE, as far back as
      frontZ = Math.min(MC.walls[0].w, MC.walls[2].w);   // the front wall allows, wide lens
      camZ = Math.max(140, frontZ - 60);
      roomD = maxZ + 40;
    } else { camZ = Math.min(3400, Math.max(maxZ + 130, dist)); roomD = Math.max(camZ * 1.85, camZ + 200); }
    t._fit = { camZ, minX, maxX, backZ: lay.closed ? frontZ : roomD - 40,
               zoomMax: lay.closed ? frontZ - 50 : roomD - 60,
               fov: lay.closed ? 58 : 46 }; t3Camera();
    // floor + ceiling + plaster sides = an enclosed home, not a void
    const fw = maxX - minX + 4, fx = (minX + maxX) / 2;
    t.woodC.repeat.set(fw / 244, (roomD + 60) / 244); t.woodN.repeat.set(fw / 244, (roomD + 60) / 244);
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(fw, roomD + 60), t.floorM);
    floor.userData = { kind: "floor" }; floor.rotation.x = -Math.PI / 2;
    floor.position.set(fx, 0, (roomD - 60) / 2 - 20); g.add(floor);
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(fw, roomD + 60),
      new THREE.MeshStandardMaterial({ color: 0xfaf8f3, roughness: .95, metalness: 0, envMapIntensity: 1.1 }));
    ceil.rotation.x = Math.PI / 2;
    ceil.position.set(fx, Hcm, (roomD - 60) / 2 - 20); ceil.userData = { kind: "env" }; g.add(ceil);
    // 4th wall behind the camera — the room is a full box you can turn around in
    // (with 4 MURAL walls the loop itself encloses the room — only fill any honest gap
    //  left when the four sizes don't form a perfect rectangle)
    if (lay.closed) {
      const last = lay.segs[3], E = last[1], P0 = lay.segs[0][0];
      const gap = Math.hypot(P0.x - E.x, P0.z - E.z);
      if (gap > 2) {
        const gw = new THREE.Mesh(new THREE.PlaneGeometry(gap, Hcm), PLASTER(gap, Hcm));
        gw.position.set((E.x + P0.x) / 2, Hcm / 2, (E.z + P0.z) / 2);
        gw.rotation.y = -Math.atan2(P0.z - E.z, P0.x - E.x);
        gw.userData = { kind: "env" }; g.add(gw);
      }
    }
    else {
    const back = new THREE.Mesh(new THREE.PlaneGeometry(fw, Hcm), PLASTER(fw, Hcm));
    back.position.set(fx, Hcm / 2, roomD - 40); back.rotation.y = Math.PI;
    back.userData = { kind: "env" }; g.add(back);
    const bsk = new THREE.Mesh(new THREE.BoxGeometry(fw, 9, 1.8), WHITE());
    bsk.position.set(fx, 4.5, roomD - 41); g.add(bsk);
    // side plaster walls from each extreme endpoint to the camera line
    for (const sideX of [minX, maxX]) {
      const zStart = Math.max(0, ...lay.segs.flatMap(s => s.filter(p => Math.abs(p.x - sideX) < .5).map(p => p.z)));
      const len = roomD - 40 - zStart; if (len < 40) continue;
      const side = new THREE.Mesh(new THREE.PlaneGeometry(len, Hcm), PLASTER(len, Hcm));
      side.position.set(sideX, Hcm / 2, zStart + len / 2);
      side.rotation.y = sideX === minX ? Math.PI / 2 : -Math.PI / 2;
      side.userData = { kind: "env" }; g.add(side);
      const sk = new THREE.Mesh(new THREE.BoxGeometry(len, 9, 1.8), WHITE());
      sk.position.set(sideX === minX ? sideX + 1 : sideX - 1, 4.5, zStart + len / 2);
      sk.rotation.y = side.rotation.y; g.add(sk);
      const sh = new THREE.Mesh(new THREE.PlaneGeometry(len, 26), t.shadowM);
      sh.rotation.x = -Math.PI / 2; sh.rotation.z = side.rotation.y;
      sh.position.set(sideX === minX ? sideX + 13 : sideX - 13, .6, zStart + len / 2); g.add(sh);
    }
    }
    // mural walls
    lay.segs.forEach((seg, k) => {
      const wl = MC.walls[k], A = seg[0], B = seg[1];
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(wl.w, Hcm), null);
      if (win) { const { t: tc } = wallTexture(win, k, true);
        const ct = new THREE.CanvasTexture(tc); ct.anisotropy = t.r.capabilities.getMaxAnisotropy();
        ct.encoding = THREE.sRGBEncoding; t.texes.push(ct);
        mesh.material = new THREE.MeshStandardMaterial({ map: ct, roughness: .82, metalness: 0, envMapIntensity: .5 });
      } else mesh.material = PLASTER();
      mesh.position.set((A.x + B.x) / 2, Hcm / 2, (A.z + B.z) / 2);
      mesh.rotation.y = -Math.atan2(B.z - A.z, B.x - A.x);
      mesh.userData = { kind: "wall", k }; g.add(mesh); t.wallMeshes[k] = mesh;
      (wl.cutouts || []).forEach((co, ci) => t3Opening(mesh, wl, co, k, ci));
      const len = Math.hypot(B.x - A.x, B.z - A.z);
      const sh = new THREE.Mesh(new THREE.PlaneGeometry(len, 26), t.shadowM);
      sh.rotation.x = -Math.PI / 2; sh.rotation.z = mesh.rotation.y;
      const nx = -(B.z - A.z) / len, nz = (B.x - A.x) / len;   // inward normal (left of A->B)
      sh.position.set((A.x + B.x) / 2 + nx * 13, .5, (A.z + B.z) / 2 + nz * 13); g.add(sh);
      const lab = document.createElement("canvas"); lab.width = 256; lab.height = 48;
      const lx3 = HERO ? null : lab.getContext("2d");   // hero capture: no cm captions in the frame
      if (lx3) { lx3.font = "500 26px " + (getComputedStyle(document.body).fontFamily || "sans-serif");
        lx3.fillStyle = "#84806f"; lx3.textAlign = "center"; lx3.fillText(`${wl.w} cm`, 128, 33);
        const lt3 = new THREE.CanvasTexture(lab); t.texes.push(lt3);
        const lm3 = new THREE.Mesh(new THREE.PlaneGeometry(96, 18),
          new THREE.MeshBasicMaterial({ map: lt3, transparent: true }));
        lm3.position.set((A.x + B.x) / 2 + nx * 2.5, 13, (A.z + B.z) / 2 + nz * 2.5);
        lm3.rotation.y = mesh.rotation.y; g.add(lm3); }
      if (MC.sel === k && MC._flashUntil && Date.now() < MC._flashUntil) {
        const e = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry),
          new THREE.LineBasicMaterial({ color: 0x4a5230 }));
        e.position.copy(mesh.position); e.rotation.copy(mesh.rotation); e.position.y += .5; g.add(e);
        clearTimeout(MC._flashT); MC._flashT = setTimeout(draw, 750); }
    });
    t._opKey = "";                     // chips rebuilt hidden — restore the active one
    if (t._activeOp) opShow(t._activeOp);
    t3Render(); }
  function t3Draw(win) {
    // three tiers, cheapest wins: camera-only (look-around) -> texture-only (pan/mood/
    // tone/offset: update wall images in place, NO geometry churn) -> full rebuild
    const geoSig = JSON.stringify([MC.walls, MC.h, MC.sel, MC._flashUntil > Date.now(),
      contW(), win ? win.cv.height : 0, !win]);
    const texSig = JSON.stringify([_ckey, MC.offset, MC.panel, MC.grid,
      win && win.winW, win && win.short]);
    if (T3.room && T3._geoSig === geoSig) {
      if (T3._texSig !== texSig && win) {
        T3._texSig = texSig;
        T3.wallMeshes.forEach((m, k) => { if (!m || !m.material || !m.material.map) return;
          const { t: tc } = wallTexture(win, k, true);
          m.material.map.image = tc; m.material.map.needsUpdate = true; }); }
      t3Camera(); t3Render(); return; }
    T3._geoSig = geoSig; T3._texSig = texSig; t3Build(win); }
  function t3Cast(ev) {   // pointer -> scene hit, mirroring the 2D hit() priorities
    const t = T3, r = t.cv.getBoundingClientRect(), p = ev.touches ? ev.touches[0] : ev;
    const m = new THREE.Vector2((p.clientX - r.left) / r.width * 2 - 1, -((p.clientY - r.top) / r.height) * 2 + 1);
    t.ray.setFromCamera(m, t.cam);
    const hits = t.ray.intersectObjects(t.room.children, true);
    for (const h of hits) {
      let o = h.object; while (o && (!o.userData || !o.userData.kind)) o = o.parent;
      if (!o) continue;
      const ud = o.userData;
      if (ud.kind === "chip") { if (!o.visible) continue;   // hidden controls are inert
        return { kind: ud.op, k: ud.k, ci: ud.ci }; }
      if (ud.kind === "wall") { const wl = MC.walls[ud.k];
        // hit may be on opening geometry (a child) — convert the world point into the
        // wall plane's local space so cm coords are always wall-true
        const lp = t.wallMeshes[ud.k].worldToLocal(h.point.clone());
        const cmX = lp.x + wl.w / 2, cmY = lp.y + MC.h / 2;
        for (let ci = (wl.cutouts || []).length - 1; ci >= 0; ci--) { const co = wl.cutouts[ci];
          if (cmX >= co.x && cmX <= co.x + co.w && cmY >= co.y && cmY <= co.y + co.h)
            return { kind: "comove", k: ud.k, ci, dx: cmX - co.x, dy: cmY - co.y }; }
        return { kind: "pan", k: ud.k, startOffset: MC.offset, startX: p.clientX, moved: false }; }
      if (ud.kind === "floor" || ud.kind === "env")
        return { kind: "yaw", x0: p.clientX, yaw0: MC.yaw || 0, moved: false };
    }
    return { kind: "yaw", x0: p.clientX, yaw0: MC.yaw || 0, moved: false }; }
  function t3AnyWall(ev) {   // pointer -> ANY wall's cm coords (openings can change walls)
    const t = T3, r = t.cv.getBoundingClientRect(), p = ev.touches ? ev.touches[0] : ev;
    const m = new THREE.Vector2((p.clientX - r.left) / r.width * 2 - 1, -((p.clientY - r.top) / r.height) * 2 + 1);
    t.ray.setFromCamera(m, t.cam);
    const h = t.ray.intersectObjects(t.wallMeshes.filter(Boolean), false)[0];
    if (!h) return null;
    const k = t.wallMeshes.indexOf(h.object);
    return { k, cmX: h.uv.x * MC.walls[k].w, cmY: h.uv.y * MC.h }; }
  function t3WallCm(ev, k) {   // current pointer position in wall-k centimetres (or null)
    const t = T3, r = t.cv.getBoundingClientRect(), p = ev.touches ? ev.touches[0] : ev;
    const m = new THREE.Vector2((p.clientX - r.left) / r.width * 2 - 1, -((p.clientY - r.top) / r.height) * 2 + 1);
    t.ray.setFromCamera(m, t.cam);
    const h = t.ray.intersectObject(t.wallMeshes[k], false)[0];
    return h ? { cmX: h.uv.x * MC.walls[k].w, cmY: h.uv.y * MC.h } : null; }
  // ----- walk-in mode: 360° look + first-person movement (joystick / WASD) -----
  function wkToggle() {
    if (!T3 || T3.fail || !T3.room) { toast("Put an artwork on the wall first."); return; }
    const b = $("#wlmWalkB");
    if (T3._walk) { T3._walk = null; disp("#wlmJoy", "none");
      if (b) b.classList.remove("on"); draw(); return; }
    T3._walk = { x: 0, z: Math.max(150, T3._fit.backZ * .55), yaw: 0, pitch: 0, keys: {}, joy: null, last: 0 };
    disp("#wlmJoy", "block");
    if (b) b.classList.add("on");
    draw();   // walking raises the texture floor — rebuild walls at close-up sharpness
    toast("You are in the room — drag to look around, joystick (or W A S D) to walk. Tap the walk icon to step out.", 5200);
    requestAnimationFrame(wkLoop); }
  function wkLoop(ts) {
    const t = T3, w = t && t._walk; if (!w) return;
    if (MC.tab !== "tpl") { requestAnimationFrame(wkLoop); return; }   // idle while on other tabs
    const dt = Math.min(.05, (ts - (w.last || ts)) / 1000); w.last = ts;
    let fwd = 0, str = 0, turn = 0;
    if (w.keys.w || w.keys.arrowup) fwd += 1;
    if (w.keys.s || w.keys.arrowdown) fwd -= 1;
    if (w.keys.a) str -= 1; if (w.keys.d) str += 1;
    if (w.keys.arrowleft) turn -= 1; if (w.keys.arrowright) turn += 1;
    if (w.joy) { fwd += -w.joy[1]; turn += w.joy[0]; }
    w.yaw += turn * dt * 1.7;
    const sp = 340 * dt, fx = Math.sin(w.yaw), fz = -Math.cos(w.yaw);
    w.x += (fx * fwd + Math.cos(w.yaw) * str) * sp;
    w.z += (fz * fwd + Math.sin(w.yaw) * str) * sp;
    t3Camera(); t3Render();
    requestAnimationFrame(wkLoop); }
  window.addEventListener("keydown", e => { const w = T3 && T3._walk; if (!w) return;
    const k = e.key.toLowerCase();
    if (["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k)) {
      w.keys[k] = 1; e.preventDefault(); } });
  window.addEventListener("keyup", e => { const w = T3 && T3._walk; if (!w) return;
    w.keys[e.key.toLowerCase()] = 0; });
  (() => { const joy = $("#wlmJoy"), nub = $("#wlmJoyN");
    joy.onpointerdown = e => { joy.setPointerCapture(e.pointerId); joy._on = 1; joyMove(e); };
    joy.onpointermove = e => { if (joy._on) joyMove(e); };
    joy.onpointerup = joy.onpointercancel = () => { joy._on = 0;
      if (T3 && T3._walk) T3._walk.joy = null; nub.style.transform = "translate(-50%,-50%)"; };
    function joyMove(e) { const r = joy.getBoundingClientRect();
      let nx = ((e.clientX - r.left) / r.width - .5) * 2, ny = ((e.clientY - r.top) / r.height - .5) * 2;
      const m = Math.hypot(nx, ny); if (m > 1) { nx /= m; ny /= m; }
      if (T3 && T3._walk) T3._walk.joy = [nx, ny];
      nub.style.transform = `translate(calc(-50% + ${nx * 26}px), calc(-50% + ${ny * 26}px))`; } })();
  function t3Wire() {
    const cv = T3.cv;
    cv.onmousedown = cv.ontouchstart = ev => { if (MC.tab !== "tpl") return;
      if (T3._walk) { const p = ev.touches ? ev.touches[0] : ev;
        if (ev.target === $("#wlmJoy")) return;
        cv._lk = { x0: p.clientX, y0: p.clientY, yaw0: T3._walk.yaw, pitch0: T3._walk.pitch, moved: false };
        ev.preventDefault(); return; }
      cv._d3 = t3Cast(ev); ev.preventDefault(); };
    cv.onwheel = ev => { if (MC.tab !== "tpl" || T3._walk) return;
      ev.preventDefault();
      MC.zoom = Math.max(.55, Math.min(1.9, (MC.zoom || 1) * (ev.deltaY > 0 ? 1.07 : 1 / 1.07)));
      t3Camera(); t3Render(); };
    cv.onmousemove = cv.ontouchmove = ev => {
      if (MC.tab !== "tpl") return;
      if (ev.touches && ev.touches.length === 2 && !T3._walk) { ev.preventDefault();   // pinch = dolly
        const dNow = Math.hypot(ev.touches[0].clientX - ev.touches[1].clientX,
                                ev.touches[0].clientY - ev.touches[1].clientY);
        if (cv._pz) { MC.zoom = Math.max(.55, Math.min(1.9, (MC.zoom || 1) * cv._pz / dNow));
          t3Camera(); t3Render(); }
        cv._pz = dNow; cv._d3 = null; return; }
      if (T3._walk) { if (!cv._lk) { cv.style.cursor = "grab"; return; }
        const p = ev.touches ? ev.touches[0] : ev, w = T3._walk;
        if (Math.abs(p.clientX - cv._lk.x0) + Math.abs(p.clientY - cv._lk.y0) > 5) cv._lk.moved = true;
        w.yaw = cv._lk.yaw0 - (p.clientX - cv._lk.x0) * .0035;
        w.pitch = Math.max(-.45, Math.min(.45, cv._lk.pitch0 + (p.clientY - cv._lk.y0) * .0022));
        ev.preventDefault(); return; }
      if (!cv._d3) { const d = t3Cast(ev);
        cv.style.cursor = d.kind === "pan" ? "grab" : d.kind === "yaw" ? "ew-resize"
          : d.kind === "cosize" ? "nwse-resize" : d.kind === "codel" ? "pointer" : "move";
        opShow(d.kind === "comove" || d.kind === "cosize" || d.kind === "codel"
          ? { k: d.k, ci: d.ci } : T3._activeOp || null);
        return; }
      const d = cv._d3, p = ev.touches ? ev.touches[0] : ev;
      if (d.kind === "yaw") { if (Math.abs(p.clientX - d.x0) > 4) d.moved = true;
        MC.yaw = Math.max(-.6, Math.min(.6, d.yaw0 - (p.clientX - d.x0) / cv.clientWidth * 1.4));
        clearTimeout(cv._h3); cv._h3 = setTimeout(draw, 25); }
      else if (d.kind === "pan") { if (Math.abs(p.clientX - d.startX) > 6) d.moved = true;
        if (d.moved && !MC._short) { compose().then(comp => { if (!comp) return;
          const coverCm = comp.W / comp.H * MC.h, range = coverCm - totalW(); if (range <= 0) return;
          const cm = t3WallCm(ev, d.k); if (!cm) return;
          if (d.cm0 === undefined) { d.cm0 = cm.cmX; return; }
          MC.offset = Math.max(0, Math.min(1, d.startOffset - (cm.cmX - d.cm0) / range));
          clearTimeout(cv._h3b); cv._h3b = setTimeout(() => { draw(); }, 25);
          clearTimeout(cv._h3c); cv._h3c = setTimeout(() => { persist(); refresh(); }, 500); }); } }
      else { let cm;
        if (d.kind === "comove") { const any = t3AnyWall(ev); if (!any) return;
          if (any.k !== d.k) {                       // dragged ONTO another wall — move it
            const co2 = MC.walls[d.k].cutouts.splice(d.ci, 1)[0];
            const nw = MC.walls[any.k]; nw.cutouts = nw.cutouts || [];
            co2.x = Math.max(0, Math.min(nw.w - co2.w, Math.round(any.cmX - d.dx)));
            co2.y = Math.max(0, Math.min(MC.h - co2.h, Math.round(any.cmY - d.dy)));
            nw.cutouts.push(co2);
            d.k = any.k; d.ci = nw.cutouts.length - 1; d.w0 = undefined;
            T3._activeOp = { k: d.k, ci: d.ci };
            persist(); draw(); return; }
          cm = any; }
        else { cm = t3WallCm(ev, d.k); if (!cm) return; }
        const wl = MC.walls[d.k], co = wl.cutouts[d.ci]; if (!co) return;
        if (d.w0 === undefined) { d.w0 = co.w; d.h0 = co.h; }
        if (d.kind === "comove") {
          co.x = Math.max(0, Math.min(wl.w - co.w, Math.round(cm.cmX - d.dx)));
          co.y = Math.max(0, Math.min(MC.h - co.h, Math.round(cm.cmY - d.dy))); }
        if (d.kind === "cosize") {
          co.w = Math.max(30, Math.min(wl.w - co.x, Math.round(cm.cmX - co.x)));
          const topCm = co.y + co.h;
          let nb = Math.max(0, Math.min(topCm - 30, Math.round(cm.cmY)));
          co.y = nb; co.h = topCm - nb; }
        // live feedback = move/scale just this opening's 3D group; the exact rebuild
        // happens ONCE on release (was: full room rebuild per pointer move — the lag)
        const grp = T3.wallMeshes[d.k] && T3.wallMeshes[d.k].children[d.ci];
        if (grp) { grp.position.set(co.x + co.w / 2 - wl.w / 2, co.y + co.h / 2 - MC.h / 2, 0);
          grp.scale.set(co.w / d.w0, co.h / d.h0, 1); t3Render(); } }
      ev.preventDefault(); };
    const up3 = ev => { if (cv._lk) { const lk = cv._lk; cv._lk = null;
        if (!lk.moved && ev && ev.clientX !== undefined && ev.target === cv) { const d = t3Cast(ev);   // click in walk mode = chips still work
          if (d && d.kind === "codel") { (MC.walls[d.k].cutouts || []).splice(d.ci, 1);
            persist(); draw(); toast("Opening removed."); } }
        return; }
      if (cv._d3) { const d = cv._d3;
      if (d.kind === "codel") { (MC.walls[d.k].cutouts || []).splice(d.ci, 1);
        T3._activeOp = null; persist(); draw(); toast("Opening removed."); }
      else if (d.kind === "comove" && !d.moved) {   // tap an opening = reveal its controls
        T3._activeOp = { k: d.k, ci: d.ci }; opShow(T3._activeOp); }
      else if (d.kind === "pan" && !d.moved) { MC.sel = d.k; MC._flashUntil = Date.now() + 700;
        T3._activeOp = null; draw(); }
      else if (d.kind !== "yaw") {
        if (d.kind === "comove" || d.kind === "cosize") T3._activeOp = { k: d.k, ci: d.ci };
        persist(); refresh(); } }
      cv._d3 = null; cv._pz = null; };
    window.addEventListener("mouseup", up3); cv.ontouchend = up3; }

  // ---------- My room (photo, perspective) ----------
  function drawOwn(cv, win) {
    if (!_ownImg) { loadImg(MC.ownSrc).then(im => { _ownImg = im; draw(); }); return; }
    const W = contW(), H = Math.round(W * _ownImg.height / _ownImg.width);
    cv.width = W; cv.height = H;
    const x = cv.getContext("2d"); x.imageSmoothingQuality = "high"; x.drawImage(_ownImg, 0, 0, W, H);
    if (!MC.ownQuad) MC.ownQuad = [[0.06, 0.08], [0.94, 0.08], [0.94, 0.9], [0.06, 0.9]];
    const q = MC.ownQuad;
    if (win && !MC.ownEdit) {
      // plane size in PIXEL space (normalized coords ignored the photo's aspect ratio —
      // that skewed planeW and let the full mural run bury the photo)
      const qq = q.map(p => [p[0] * W, p[1] * H]);
      const wpx = (Math.hypot(qq[1][0] - qq[0][0], qq[1][1] - qq[0][1]) + Math.hypot(qq[2][0] - qq[3][0], qq[2][1] - qq[3][1])) / 2;
      const hpx = (Math.hypot(qq[3][0] - qq[0][0], qq[3][1] - qq[0][1]) + Math.hypot(qq[2][0] - qq[1][0], qq[2][1] - qq[1][1])) / 2;
      const planeW = MC.ownH * wpx / hpx;               // photographed wall width, cm
      const tw = totalW();
      const visW = Math.min(planeW, tw);                // cm of the run this wall can show
      MC.ownOff = Math.max(0, Math.min(MC.ownOff || 0, tw - visW));
      const cropCm = Math.min(MC.h, MC.ownH);           // taller murals: floor-anchored crop
      const sy = (MC.h - cropCm) / MC.h * win.cv.height;
      const sx = MC.ownOff / tw * win.cv.width, sw = Math.max(1, visW / tw * win.cv.width);
      const slice = document.createElement("canvas");
      slice.width = Math.round(sw); slice.height = Math.max(1, Math.round(win.cv.height - sy));
      slice.getContext("2d").drawImage(win.cv, sx, sy, sw, win.cv.height - sy, 0, 0, slice.width, slice.height);
      _ownSlice = slice;                                // AI photo sends exactly this view
      const uw = visW / planeW, u0 = (1 - uw) / 2;      // centred if narrower than the wall
      const vh = cropCm / MC.ownH;                      // floor-anchored on the marked wall
      const Hm = solveH([[0, 0], [1, 0], [1, 1], [0, 1]], qq);
      const dst = [[u0, 1 - vh], [u0 + uw, 1 - vh], [u0 + uw, 1], [u0, 1]].map(p => applyH(Hm, p[0], p[1]));
      x.save(); x.beginPath(); x.moveTo(qq[0][0], qq[0][1]);
      qq.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.clip();
      drawWarped(x, slice, dst); x.restore();
      // drag sideways = choose which part of the run shows (true size, never stretched)
      if (tw > planeW + 1) {
        if (!MC._slideHinted) { MC._slideHinted = 1; toast("Drag the mural sideways to choose which part of it shows.", 4000); }
        cv.style.cursor = "grab"; cv.style.touchAction = "pan-y"; cv.onmousedown = null; cv.ontouchstart = null;
        cv.onpointerdown = ev => { try { cv.setPointerCapture(ev.pointerId) } catch (e) {}
          cv._ox = ev.clientX; ev.preventDefault();
          cv.onpointermove = e2 => { if (cv._ox === undefined) return;
            const r = cv.getBoundingClientRect();
            const dcm = (e2.clientX - cv._ox) / (wpx * r.width / W) * planeW;
            cv._ox = e2.clientX;
            MC.ownOff = Math.max(0, Math.min(tw - visW, MC.ownOff - dcm)); draw(); };
          cv.onpointerup = cv.onpointercancel = () => { cv._ox = undefined; cv.onpointermove = null; }; }; }
      else { cv.style.cursor = ""; cv.style.touchAction = ""; cv.onpointerdown = null; cv.onmousedown = null; cv.ontouchstart = null; } }
    if (MC.ownEdit) { const qq = q.map(p => [p[0] * W, p[1] * H]);
      x.strokeStyle = "rgba(74,82,48,.85)"; x.setLineDash([6, 5]); x.beginPath();
      x.moveTo(qq[0][0], qq[0][1]); qq.slice(1).forEach(p => x.lineTo(p[0], p[1])); x.closePath(); x.stroke(); x.setLineDash([]);
      qq.forEach(p => { x.beginPath(); x.arc(p[0], p[1], 11, 0, 7); x.fillStyle = "rgba(74,82,48,.92)"; x.fill(); });
      const pos = ev => { const r = cv.getBoundingClientRect(); const t = ev.touches ? ev.touches[0] : ev;
        return [(t.clientX - r.left) / r.width, (t.clientY - r.top) / r.height]; };
      cv.onmousedown = cv.ontouchstart = ev => { const [px, py] = pos(ev);
        for (let k = 0; k < 4; k++) if (Math.hypot(q[k][0] - px, q[k][1] - py) < .035) { cv._pd = k; ev.preventDefault(); } };
      cv.onmousemove = cv.ontouchmove = ev => { if (cv._pd === undefined || cv._pd === null) return;
        MC.ownQuad[cv._pd] = pos(ev); draw(); ev.preventDefault(); };
      window.addEventListener("mouseup", () => cv._pd = null); cv.ontouchend = () => cv._pd = null;
      cv.onpointerdown = null; cv.style.cursor = "";
      disp("#wlmGuide", "flex"); }
    else if (!win) { cv.onmousedown = null; cv.onpointerdown = null; cv.style.cursor = ""; } }

  // ---------- zoom lightbox ----------
  async function zoomOpen() {
    const comp = await compose(); if (!comp) { toast("Add an artwork first."); return; }
    const win = windowOfCached(comp); const img = win.short ? comp.cv : win.cv;
    let ov = document.getElementById("wlmzoom");
    if (!ov) { ov = document.createElement("div"); ov.id = "wlmzoom";
      ov.style.cssText = "position:fixed;inset:0;z-index:2147482800;background:rgba(15,15,13,.94);display:flex;flex-direction:column";
      ov.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center;padding:10px 14px;color:#eee;font-size:13px">
        <span>Every detail — drag to move, pinch or scroll to zoom.</span>
        <span><button id="wlmzMinus" style="width:34px;height:34px;font-size:16px;cursor:pointer">−</button>
        <button id="wlmzPlus" style="width:34px;height:34px;font-size:16px;cursor:pointer">+</button>
        <button id="wlmzX" style="height:34px;padding:0 14px;cursor:pointer">Close ✕</button></span></div>
        <canvas id="wlmzCv" style="flex:1;width:100%;touch-action:none"></canvas>`;
      document.body.appendChild(ov); }
    ov.style.display = "flex";
    const cv = ov.querySelector("#wlmzCv");
    const Z = { s: 1, x: 0, y: 0 };
    const fit = () => { cv.width = ov.clientWidth; cv.height = ov.clientHeight - 54;
      Z.base = Math.min(cv.width / img.width, cv.height / img.height); };
    const paint = () => { const x = cv.getContext("2d");
      x.fillStyle = "#0f0f0d"; x.fillRect(0, 0, cv.width, cv.height);
      const s = Z.base * Z.s, w = img.width * s, h = img.height * s;
      const cx = cv.width / 2 + Z.x, cy = cv.height / 2 + Z.y;
      x.imageSmoothingQuality = "high"; x.drawImage(img, cx - w / 2, cy - h / 2, w, h); };
    fit(); paint();
    const clampPan = () => { const s = Z.base * Z.s;
      const mx = Math.max(0, (img.width * s - cv.width) / 2 + 40), my = Math.max(0, (img.height * s - cv.height) / 2 + 40);
      Z.x = Math.max(-mx, Math.min(mx, Z.x)); Z.y = Math.max(-my, Math.min(my, Z.y)); };
    const zoomTo = f => { Z.s = Math.max(1, Math.min(8, Z.s * f)); clampPan(); paint(); };
    ov.querySelector("#wlmzPlus").onclick = () => zoomTo(1.4);
    ov.querySelector("#wlmzMinus").onclick = () => zoomTo(1 / 1.4);
    ov.querySelector("#wlmzX").onclick = () => ov.style.display = "none";
    cv.onwheel = e => { e.preventDefault(); zoomTo(e.deltaY < 0 ? 1.15 : 1 / 1.15); };
    const pts = new Map();
    cv.onpointerdown = e => { try{cv.setPointerCapture(e.pointerId)}catch(x){} pts.set(e.pointerId, [e.clientX, e.clientY]); };
    cv.onpointermove = e => { if (!pts.has(e.pointerId)) return;
      const prev = pts.get(e.pointerId); pts.set(e.pointerId, [e.clientX, e.clientY]);
      if (pts.size === 1) { Z.x += e.clientX - prev[0]; Z.y += e.clientY - prev[1]; clampPan(); paint(); }
      else if (pts.size === 2) { const [a, b] = [...pts.values()];
        const dNow = Math.hypot(a[0] - b[0], a[1] - b[1]);
        if (cv._pd2) zoomTo(dNow / cv._pd2);
        cv._pd2 = dNow; } };
    cv.onpointerup = cv.onpointercancel = e => { pts.delete(e.pointerId); cv._pd2 = null; };
    window.addEventListener("resize", () => { if (ov.style.display !== "none") { fit(); paint(); } }); }

  // ---------- actions ----------
  async function aiRun() {
    if (!QUOTE) { toast("Finish your mural first."); return; }
    if (!MC.ownSrc) { toast("Upload a photo of your room first."); return; }
    const comp = await compose(); const win = windowOfCached(comp);
    if (win.short) { toast("Add more artworks first — the blend must cover your walls."); return; }
    toast("Creating your photo — 20–40 s, please wait…", 9000);
    // room = the CLEAN uploaded photo (the on-screen canvas already has the mural
    // composited on it — sending that gave the model no room to work with);
    // art = the exact slice the customer sees on their wall (set by drawOwn)
    const art = (_ownSlice || win.cv).toDataURL("image/jpeg", .9);
    let r; try { r = await api("ai-mockup", { email: EMAIL || "", image: MC.ownSrc,
      own_art: art, framing: "rolled", mural: true });
    } catch (e) { toast("The connection dropped while creating your photo — please try again.", 6000); return; }
    if (!r.ok) { toast(r.message || "The preview did not come back — nothing was spent.", 6000); return; }
    MC.aiUrl = r.url;
    if (MC.tab !== "ai") { MC.tab = "ai"; tabsUI(); draw(); }  // finished render must be seen
    paneUI(); }
  async function atcRun() {
    if (!QUOTE || !MC.members.length) { toast("Add an artwork to your mural first."); return; }
    const b = $("#wlmAtc"); b.disabled = true; b.textContent = "Adding to your cart…";
    try {
      const nl = await fetch(API + "/api/native-line", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: "mural", indices: MC.members, moods: MC.members.map(() => MC.mood),
          material: MC.material, panel_width: typeof MC.panel === "number" ? MC.panel : 0,
          offset: Math.round(MC.offset * 1000) / 1000, w: totalW(), h: MC.h,
          walls: wallsPayload(), tone: MC.tone }) }).then(r => r.json());
      if (!nl.ok) { toast(nl.message || "Could not price the mural — please try again."); b.disabled = false; b.textContent = "Add mural to cart"; return; }
      const cr = await fetch(location.origin + "/cart/add.js", { method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ items: [{ id: nl.variantId, quantity: 1, properties: nl.properties }] }) });
      if (!cr.ok) throw new Error("cart add " + cr.status);
      b.textContent = "✓ Added — taking you to your cart";
      setTimeout(() => location.href = "/cart", 700);
    } catch (e) { console.warn("wl mural atc", e); toast("Something interrupted the add-to-cart — please try again.");
      b.disabled = false; b.textContent = "Add mural to cart"; } }
  function saveImage() { try {
      const a = document.createElement("a"); a.download = "wall-liberation-mural.jpg";
      a.href = viewCanvas().toDataURL("image/jpeg", .9); a.click(); toast("Saved to your downloads.");
    } catch (e) { toast("Could not save the preview — try again."); } }
  async function shareRun() {
    const comp = await compose(); if (!comp) { toast("Add an artwork first."); return; }
    const win = windowOfCached(comp); if (win.short) { toast("Finish the blend first."); return; }
    const r = await api("share", { image: win.cv.toDataURL("image/jpeg", .8),
      title: `Wall mural — ${MC.members.length} artwork${MC.members.length > 1 ? "s" : ""}`,
      index: MC.members[0] || null, cm: `${MC.walls.map(w => w.w).join("+")}×${MC.h} cm`,
      price: QUOTE ? money(QUOTE.discounted) : "", kind: "mural",
      // full design state — lets the studio panel list this design and publish it
      // as a pre-designed product (same fields as the native-line recipe input)
      state: { indices: MC.members, moods: MC.members.map(() => MC.mood),
        walls: wallsPayload(), h: MC.h, panel: typeof MC.panel === "number" ? MC.panel : 0,
        material: MC.material, offset: Math.round(MC.offset * 1000) / 1000, tone: MC.tone } });
    if (!r.ok) { toast("Could not build the link — try again."); return; }
    const row = $("#wlmShareRow"); row.style.display = "flex";
    $("#wlmShareL").value = "https://www.wallliberation.com.au/apps/studio" + r.url; }
  async function emailRun() {
    const row = $("#wlmEmailRow");
    if (row.style.display !== "flex") { row.style.display = "flex"; $("#wlmEmailIn").value = EMAIL; $("#wlmEmailIn").focus(); return; }
    const em = ($("#wlmEmailIn").value || "").trim();
    if (!/.+@.+\..+/.test(em)) { toast("Enter the email we should send it to."); return; }
    EMAIL = em; try { localStorage.wl_email = em; } catch (e) {}
    let snap; try { snap = viewCanvas().toDataURL("image/jpeg", .85); } catch (e) { toast("Could not read the preview — try again."); return; }
    const r = await api("mockup-email", { email: em, image: snap, index: MC.members[0] || "" });
    toast(r.message || "Sent."); row.style.display = "none"; }
  function infoPopup() {
    let ov = document.getElementById("wlmguide2");
    if (!ov) {
      ov = document.createElement("div"); ov.id = "wlmguide2";
      ov.style.cssText = "position:fixed;inset:0;z-index:2147482900;background:rgba(20,20,20,.55);display:flex;align-items:center;justify-content:center;padding:16px";
      ov.innerHTML = `<div style="background:#fff;max-width:560px;width:100%;max-height:88vh;overflow:auto;padding:26px 28px;font-family:inherit;font-size:13.5px;line-height:1.65;color:#2b2925">
        <h3 style="font-weight:600;font-size:1.1rem;margin:0 0 4px">Your wall mural — how it works</h3>
        <p style="margin:0 0 14px;color:#6b6659;font-size:12px">Design a mural for one wall or a whole room, and preview it before you buy.</p>
        <p style="margin:0 0 2px"><b>Demo room</b></p>
        <p style="margin:0 0 12px">A real room grows with your wall sizes — up to a whole room of four walls. Pick a theme or tap artworks to fill the walls — the blend flows around corners. Click a wall to select it, add windows or doors, and drag the mural to choose which part shows. Scroll or pinch to zoom, drag the floor to look around, and the walk icon steps you inside the room.</p>
        <p style="margin:0 0 2px"><b>Cutting</b></p>
        <p style="margin:0 0 12px">Every measurement is in millimetres. Drag the green markers on the rulers to place each cut exactly — that is exactly how the mural is cut. Or choose one seamless piece per wall.</p>
        <p style="margin:0 0 2px"><b>My room &amp; AI photo</b></p>
        <p style="margin:0 0 12px">Upload a photo of your own wall, mark its corners and set the height — the mural appears in your room at true size. The AI photo turns the same view into a photoreal picture. Up to 5 free photos a day.</p>
        <p style="margin:0 0 2px"><b>What you approve is what we print</b></p>
        <p style="margin:0 0 12px">Your mural is produced from the exact view on your screen — the same blend, colours, crop and adjustments — and the artist reviews it at full print resolution before it goes to press. Windows and doors you marked are printed through and trimmed on site during hanging, so a small measuring difference never leaves a gap.</p>
        <p style="margin:0 0 2px"><b>Hanging</b></p>
        <p style="margin:0 0 12px">Panels arrive numbered, machine-cut to your exact lines, and go up left to right, edge to edge — no overlap, no trimming between panels. Step-by-step hanging instructions come in the box. Paste is not included.</p>
        <p style="margin:0 0 2px"><b>Your privacy</b></p>
        <p style="margin:0 0 16px">Your room photo is used only to build your preview. It is not shared, and you can replace or remove it at any time.</p>
        <button class="wlgx" style="border:1px solid #1b1b1b;background:#1b1b1b;color:#fff;padding:9px 18px;font-size:11px;letter-spacing:.06em;cursor:pointer;font-family:inherit">Got it</button></div>`;
      document.body.appendChild(ov);
      ov.onclick = e => { if (e.target === ov) ov.style.display = "none"; };
      ov.querySelector(".wlgx").onclick = () => ov.style.display = "none";
    } else ov.style.display = "flex"; }

  // ---------- boot ----------
  async function boot() {
    try {
      const [dz, st, th] = await Promise.all([
        fetch(API + "/api/designs").then(r => r.json()),
        fetch(API + "/api/mural/stats").then(r => r.json()).catch(() => ({})),
        fetch(API + "/api/mural/themes").then(r => r.json()).catch(() => ({ themes: [] }))]);
      DESIGNS = dz.designs || []; STATS = st || {}; THEMES = (th && th.themes) || [];
    } catch (e) { console.warn("wl mural boot", e);
      $("#wlmEcho").textContent = "The mural designer could not load its catalogue — please refresh."; return; }
    MC.members = (MC.members || []).filter(i => byI(i)).slice(0, 12);
    const seed = SEED0;
    if (seed && (PDP_FRESH || HERO)) {   // pre-designed mural, first visit (or stale pocket):
      // present the CURATED design pristine — artworks from the seed, everything else at
      // catalogue defaults. The customer's edits persist per-product from here (SKEY pocket).
      MC.members = [];
      seed.split(/[\s,]+/).filter(i => byI(i)).slice(0, 12).forEach(i => MC.members.push(i));
      MC.mood = "natural"; MC.tone = { b: 1, c: 1, s: 1 }; MC.h = 270;
      MC.panel = 100; MC.material = "matte_canvas"; MC.offset = .5; MC.grid = true;
      MC.walls = [{ w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }, { w: 400, cuts: null, cutouts: [] }];
      MC.sel = 0;
      if (SIMPLE)   // single artwork carries one wall — open at a size it prints alone
        MC.walls = [{ w: Math.min(MAXW || 300, 300), cuts: null, cutouts: [] }];
      if (HERO) {   // hero capture can pin the wall: &wlmw=300&wlmh=270 (single-artwork heroes)
        const hq = new URLSearchParams(location.search);
        const hw = +hq.get("wlmw") || 0, hh = +hq.get("wlmh") || 0;
        if (hw) MC.walls = [{ w: Math.max(100, Math.min(1200, hw)), cuts: null, cutouts: [] }];
        if (hh) MC.h = Math.max(200, Math.min(320, hh));
        const hz = +hq.get("wlmz") || 0;   // camera dolly for narrow single walls (<1 = closer)
        if (hz) MC._hz = Math.max(.4, Math.min(2.5, hz)); }
      _ckey = ""; persist(); }   // reloads of the SAME product keep the customer's edits
    if (root.dataset.embed === "product") {   // PDP renders its own SERVER-SIDE title/desc
      const t2 = $(".wlm-title"), l2 = $(".wlm-lede");
      if (t2) t2.remove(); if (l2) l2.remove(); }
    tabsUI(); paneUI(); orderUI(); ctrlsUI(); filtersUI(); themesUI(); cardsUI(); refresh();
    $("#wlmAuto").onclick = () => autoDress(false);
    function autoDress(quiet, poolOverride, label2) {   // one tap: dress the walls with a coherent set
      let pool = poolOverride ? poolOverride.slice() : (SR && SR.length ? SR.slice() : null);
      let label = label2 || "";
      if (!pool) { const t = THEMES[Math.floor(Math.random() * THEMES.length)] || { indices: [] };
        pool = (t.indices || []).slice(); label = t.label || ""; }
      pool = pool.filter(i => byI(i));
      if (!pool.length) { if (!quiet) toast("No artworks to pick from yet."); return; }
      // take enough pieces to actually COVER the wall run at full height (never stretched):
      // width of each piece at full height = height × aspect (r is long/short)
      const aspect = d => d.o === "horizontal" ? (+d.r || 1.25) : d.o === "vertical" ? 1 / (+d.r || 1.25) : 1;
      const needCm = totalW() * 1.05;
      const take = []; let cover = 0;
      for (const i of pool) { if (take.length >= memberCap()) break;
        take.push(i); cover += MC.h * aspect(byI(i)) * (take.length > 1 ? (1 - OVF) : 1);
        if (take.length >= 2 && cover >= needCm) break; }
      MC.members = take;
      _ckey = ""; persist(); orderUI(); cardsUI(); refresh();
      if (!quiet) toast(label ? `Dressed with “${label}” — swap anything you like.` : "Dressed from your results — swap anything you like.", 4000); }
    // curated EXAMPLE ROOMS (real theme sets) — one-tap starting points to swap and change
    // examples row: new keys first, pre-2026-07-30 keys as fallback so the row never
    // vanishes while a customer's browser still holds the hour-cached old themes JSON
    let EX = ["nympheas", "turner", "attersee"].map(k => THEMES.find(t => t.key === k)).filter(Boolean);
    if (!EX.length) EX = ["monet", "golden", "modernist"].map(k => THEMES.find(t => t.key === k)).filter(Boolean);
    let resumeBtn = "";
    try { if (localStorage.wl_mural_resume && !seed)
      resumeBtn = `<button class="wlm-mini" id="wlmResume" style="border-color:${GREEN};color:${GREEN}">↺ Resume your last design</button>`; } catch (e) {}
    if (EX.length) {
      $("#wlmEg").innerHTML = resumeBtn + `<span class="wlm-note" style="margin:0">Start from an example:</span>` +
        EX.map((t, k) => `<button class="wlm-mini" data-eg="${k}">${esc(t.label)}</button>`).join("") +
        `<span class="how">Pick the art → set your walls → we print exactly this view.</span>`;
      $$("#wlmEg [data-eg]").forEach(b => b.onclick = () => { const t = EX[+b.dataset.eg];
        autoDress(false, t.indices, t.label); });
      const rb = $("#wlmResume");
      if (rb) rb.onclick = () => { try {
        localStorage.wl_mural_state4 = localStorage.wl_mural_resume;
        localStorage.wl_mural_ts = String(Date.now());
        localStorage.removeItem("wl_mural_resume");
        location.reload(); } catch (e) {} };
    }
    // the page never opens EMPTY: first visit gets the best example, dressed and priced
    if (!MC.members.length) { if (EX.length) autoDress(true, EX[0].indices, EX[0].label); else autoDress(true); }
    $("#wlmGo").onclick = search;
    $("#wlmQ").addEventListener("keydown", e => { if (e.key === "Enter") search(); });
    $("#wlmCam").onclick = camSearch;
    $("#wlmClr").onclick = () => { $("#wlmQ").value = ""; SR = null; THEME = ""; PAGE = 1;
      $("#wlmEcho").textContent = DESIGNS.length + " artworks · newest first"; themesUI(); cardsUI(); };
    $("#wlmEcho").textContent = DESIGNS.length + " artworks · newest first";
    try { if (!localStorage.wl_mural_hint) { localStorage.wl_mural_hint = "1";
      setTimeout(() => toast("Tip — drag the floor to look around · scroll or pinch to zoom · the walk icon steps you inside.", 6500), 2500); } } catch (e) {}
    $("#wlmGridT").onchange = e => { MC.grid = e.target.checked; persist(); draw(); };
    const zstep = f => { MC.zoom = Math.max(.55, Math.min(1.9, (MC.zoom || 1) * f));
      if (T3 && !T3.fail) { t3Camera(); t3Render(); } };
    $("#wlmZin").onclick = () => zstep(1 / 1.18);
    $("#wlmZout").onclick = () => zstep(1.18);
    $("#wlmFiltT").onclick = () => { const w = $("#wlmFiltW");
      const open = w.style.display !== "block"; w.style.display = open ? "block" : "none";
      $("#wlmFiltT").textContent = open ? "Filters ▴" : "Filters ▾"; };
    $("#wlmAtc").onclick = atcRun;
    $("#wlmEmailGo").onclick = emailRun;
    $("#wlmShareC").onclick = () => { navigator.clipboard.writeText($("#wlmShareL").value).then(() => toast("Link copied")); };
    try {   // structured data for search engines / AI answers (true claims only)
      const ld = document.createElement("script"); ld.type = "application/ld+json";
      ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": [
        { "@type": "Product", "name": "Custom Wall Mural",
          "brand": { "@type": "Brand", "name": "Wall Liberation" },
          "description": "One seamless wall mural composed from more than 300 real paintings, sized to your walls to the millimetre and printed exactly as approved. Four real mural fabrics including paintable artist linen.",
          "offers": { "@type": "AggregateOffer", "priceCurrency": "AUD",
            "lowPrice": "100", "highPrice": "150",
            "description": "Per square metre: A$150/m² for the first 5 m², falling in bands to A$100/m² beyond 35 m². Shipping included." } },
        { "@type": "FAQPage", "mainEntity": [
          { "@type": "Question", "name": "How is a wall mural priced?",
            "acceptedAnswer": { "@type": "Answer", "text": "By the square metre in falling bands: the first 5 m² at A$150/m², then A$135, A$120, A$110, and everything past 35 m² at A$100/m². Shipping included — Australia, New Zealand, the US, Canada, the UK, Europe and Singapore." } },
          { "@type": "Question", "name": "Will the print match what I designed?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes — the mural is produced from the exact recipe you approved, and the artist reviews it at full print resolution before it is printed." } },
          { "@type": "Question", "name": "How does the mural arrive?",
            "acceptedAnswer": { "@type": "Answer", "text": "Rolled and insured, in numbered machine-cut panels that hang edge to edge, with step-by-step instructions in the box." } },
          { "@type": "Question", "name": "What exactly will be printed?",
            "acceptedAnswer": { "@type": "Answer", "text": "The view you approve. The on-screen preview and the print file come from the same engine to the same numbers, and every order's files are machine-checked against the approved design — exact size, every panel, every window and door — before printing." } } ] } ] });
      document.head.appendChild(ld); } catch (e) {}
    // ---------- Guided steps (Composed Walls 2026-08-13) ----------
    // Three stages over the SAME controls: 1 wall sizes · 2 art · 3 finish+approve.
    // Expert mode = today's full workshop, one click, remembered. PDP embeds and
    // hero capture keep the classic layout untouched.
    const guidedOK = !HERO;   // tabs on the page AND on mural product pages (Summer 2026-08-19)
    if (guidedOK) {
      const wlmEl = $("#wlm");
      wlmEl.classList.add("wlm-gok");
      let GSTEP = 1, expert = false;
      try { expert = localStorage.wl_mural_expert === "1"; } catch (e) {}
      const STEPS = [
        ["Define your wall", "Width and height in centimetres — add every wall of the room. We print 10 cm extra on every mural, just in case."],
        ["Select the art", "One masterpiece or several, blended into one continuous piece. Compose it for you, or pick by colour, room or artist."],
        ["Make it yours", "Surface, mood and panel cuts, in plain words — then approve exactly what we print. Add to cart below."]];
      const stepPaint = () => {
        wlmEl.classList.toggle("wlm-guided", !expert);
        ["wlm-s1", "wlm-s2", "wlm-s3"].forEach((c, k) => wlmEl.classList.toggle(c, !expert && GSTEP === k + 1));
        const bar = $("#wlmSteps"), nav = $("#wlmStepNav"), hint = $("#wlmStepHint");
        if (expert) {
          bar.innerHTML = ""; hint.textContent = "";
          nav.innerHTML = `<button class="wlm-mini" id="wlmGuidedT" title="three clear steps">‹ Guided steps</button>`;
          $("#wlmGuidedT").onclick = () => { expert = false;
            try { localStorage.wl_mural_expert = ""; } catch (e) {} stepPaint(); };
        } else {
          // SIMPLE (single-artwork PDP): two steps — the art is fixed, size comes first;
          // "Bespoke Studio" (= expert mode) is the door into the full workshop.
          const VIS = SIMPLE ? [0, 2] : [0, 1, 2];
          bar.innerHTML = VIS.map((k, n) =>
            `<button class="st ${GSTEP === k + 1 ? "on" : k + 1 < GSTEP ? "done" : ""}" data-st="${k + 1}">${n + 1} · ${STEPS[k][0]}</button>`).join("");
          hint.textContent = STEPS[GSTEP - 1][1];
          $$("#wlmSteps [data-st]").forEach(b => b.onclick = () => { GSTEP = +b.dataset.st; stepPaint(); });
          const nxStep = GSTEP === 1 && SIMPLE ? 3 : GSTEP + 1;
          nav.innerHTML =
            (GSTEP > 1 ? `<button class="wlm-mini" id="wlmStPrev">← Back</button>` : "") +
            (GSTEP < 3 ? `<button class="wlm-btn" id="wlmStNext" style="padding:10px 22px">Continue — ${STEPS[nxStep - 1][0].toLowerCase()} →</button>`
                       : `<span class="wlm-note" style="margin:0">Happy with the view? Add it to the cart below.</span>`) +
            `<button class="wlm-mini" id="wlmExpertT" style="margin-left:auto" title="${SIMPLE ? "blend this artwork with matched pieces — the full workshop" : "every control on one screen"}">${SIMPLE ? "⚒ Bespoke Studio" : "⚙ Expert mode"}</button>`;
          const nx = $("#wlmStNext"); if (nx) nx.onclick = () => { GSTEP = Math.min(3, nxStep); stepPaint(); };
          const pv = $("#wlmStPrev"); if (pv) pv.onclick = () => { GSTEP = SIMPLE && GSTEP === 3 ? 1 : Math.max(1, GSTEP - 1); stepPaint(); };
          $("#wlmExpertT").onclick = () => { expert = true;
            try { localStorage.wl_mural_expert = "1"; } catch (e) {} stepPaint(); };
          const doors = $("#wlmDoors");
          if (SIMPLE) { doors.innerHTML = ""; }
          else {
          doors.innerHTML =
            `<button class="wlm-btn" id="wlmDoor1" style="padding:9px 14px" title="fill the walls with a matched set in one tap">✨ Compose it for me</button>` +
            `<button class="wlm-mini" id="wlmDoor2" style="padding:9px 14px" title="we compose to the colours of your room">📷 From a photo of your room</button>`;
          $("#wlmDoor1").onclick = () => autoDress(false);
          $("#wlmDoor2").onclick = () => { setTab("ai");
            toast("Photograph or describe your room — the designer composes to its colours.", 5000); };
          }
        }
        draw();   // visible width can change with the tab layout
      };
      stepPaint();
    }
    // desktop sees the finish sections OPEN; phones get compact value-labelled rows
    try { if (!matchMedia("(max-width:640px)").matches)
      $$("details.wlm-acc").forEach(d => d.open = true); } catch (e) {}
    // Layout re-evaluates when the viewport crosses the phone breakpoint (2026-07-30).
    // A page booted in a hidden/prerendered/background tab reports width 0 → the phone
    // layout locks in on desktop (all-cards endless grid, no pager, closed finish
    // sections). This listener heals it the moment the real width is known, and serves
    // genuine desktop↔phone window resizes the same way.
    try { const mq = matchMedia("(max-width:640px)");
      const onbp = () => { PAGE = 1; cardsUI();
        $$("details.wlm-acc").forEach(d => d.open = !mq.matches); };
      if (mq.addEventListener) mq.addEventListener("change", onbp);
      else if (mq.addListener) mq.addListener(onbp); } catch (e) {}
    if (HERO) { MC.grid = false; MC.yaw = .12; MC.zoom = MC._hz || .96; draw(); }   // hero capture: clean room, editorial angle (&wlmz pins the dolly)
    let rs; window.addEventListener("resize", () => { clearTimeout(rs); rs = setTimeout(draw, 250); });
  }
  // hero/QA capture: render the 3D room at print-quality width and hand back a JPEG
  // data-URI (no UI chrome — the canvas holds only the room). Restores live framing after.
  window.__wlmHero = (w = 2048) => { try {
    if (!T3 || T3.fail || !T3.room) return null;
    const t = T3, H = Math.round(w * .62);
    t.r.setSize(w, H, false);
    t.cam.aspect = w / H; t.cam.updateProjectionMatrix();
    t.r.render(t.scene, t.cam);
    const url = t.cv.toDataURL("image/jpeg", .92);
    t3Camera(); t3Render();
    return url; } catch (e) { return null; } };
  // read-only debug probe (QA + any future agent): current blend tier + live texture sizes
  window.__wlmDbg = () => ({
    tier: _compHi && _hiKey === _blendKey() ? 2 : 1,
    compH: (_compHi && _hiKey === _blendKey() ? _compHi : _comp) ? (_compHi && _hiKey === _blendKey() ? _compHi.H : _comp.H) : 0,
    winH: _win ? _win.cv.height : 0,
    textures: T3 && T3.texes ? T3.texes.map(t => t.image ? [t.image.width, t.image.height] : null) : null,
    hiBuilding: !!_hiBuilding });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();
